import React, { useState, useEffect, useRef, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Form, Modal, Button, Alert, Table } from "react-bootstrap";
import WeekSelector from "../weekSelector";
import DriverSelector from "../../components/driverSelector";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { useTranslation } from 'react-i18next';
import { buildDriverWeeklyReportPdfHtml } from "../../utils/driverReportPdfHtml";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { AiOutlineLock, AiOutlineFolderOpen, AiOutlineCheckCircle } from "react-icons/ai";
import { Spinner } from "react-bootstrap";

const BATCH_PREVIEW_INITIAL_STEPS = [
    { key: "fetchDrivers", labelKey: "batchStepFetchDrivers" },
    { key: "loadingData", labelKey: "batchStepLoadingData" }
];

const BATCH_PREVIEW_DRIVER_STEPS = [
    { key: "uber", labelKey: "batchDriverStepUber", dataKey: "has_uber", invert: false },
    { key: "bolt", labelKey: "batchDriverStepBolt", dataKey: "has_bolt", invert: false },
    { key: "freenow", labelKey: "batchDriverStepFreenow", dataKey: "has_freenow", invert: false },
    { key: "extraFunds", labelKey: "batchDriverStepExtraFunds", dataKey: null, invert: false },
    { key: "debts", labelKey: "batchDriverStepDebts", dataKey: "open_debts", invert: true }
];

if (pdfFonts && pdfFonts.vfs) {
    pdfMake.vfs = pdfFonts.vfs;
}

/** Тиждень рапорту у форматі YYYYWW (напр. 202512) → число для порівняння. */
function reportWeekSortable(reportWeek) {
    if (reportWeek == null || reportWeek === "") return null;
    const s = String(reportWeek).trim();
    if (/^\d{6}$/.test(s)) return parseInt(s, 10);
    const n = parseInt(s, 10);
    if (Number.isFinite(n) && n >= 200101 && n <= 210053) return n;
    return null;
}

/** Тиждень створення боргу: або week_number як YYYYWW, або year + номер тижня 1–53. */
function debtCreationWeekSortable(debt) {
    if (!debt) return null;
    const wn = debt.week_number;
    const yr = debt.year;
    if (wn == null || wn === "") return null;
    const s = String(wn).trim();
    if (/^\d{6}$/.test(s)) return parseInt(s, 10);
    const weekNum = parseInt(s, 10);
    const yearNum = parseInt(String(yr ?? "").trim(), 10);
    if (
        Number.isFinite(weekNum) &&
        Number.isFinite(yearNum) &&
        yearNum >= 2000 &&
        yearNum <= 2100 &&
        weekNum >= 1 &&
        weekNum <= 53
    ) {
        return yearNum * 100 + weekNum;
    }
    return null;
}

/** Активний борг показуємо в рапорті лише з тижнів після тижня його створення. */
function openDebtVisibleAfterCreationWeek(debt, reportWeek) {
    const rep = reportWeekSortable(reportWeek);
    const cre = debtCreationWeekSortable(debt);
    if (rep == null || cre == null) return true;
    return rep > cre;
}

const DriverRaport = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [weekNumber, setWeekNumber] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [debts, setDebts] = useState([]); // Збереження списку боргів
    const [showDebtModal, setShowDebtModal] = useState(false);
    const [selectedDebts, setSelectedDebts] = useState([]); // Список обраних боргів
    const [finalData, setFinalData] = useState([]);
    const [totalDebt, setTotalDebt] = useState(0); // Загальна сума вибраних боргів
    const [extraFunds, setExtraFunds] = useState({});
    const [hasExtraFunds, setHasExtraFunds] = useState(null); // ✅ Стан для відображення повідомлення
    /** План списання після «Застосувати» в модалці: повні id + опційно один частковий */
    const [partialSettlement, setPartialSettlement] = useState(null); // { fullCloseIds: [], partial: { debtId, amountPaid, remainder, debtName, originalTotal } | null }
    /** Знімок боргів для PDF після «Застосувати» (partialSettlement скидається після збереження чернетки). */
    const [appliedDebtsSnapshot, setAppliedDebtsSnapshot] = useState([]);
    const [salaryInput, setSalaryInput] = useState(0); // Введена сума зарплати
    const [isSalaryUpdating, setIsSalaryUpdating] = useState(false); // Статус збереження
    const [showSalaryDistribution, setShowSalaryDistribution] = useState(false); // Показ/приховування блоку розподілу
    // Batch створення чернеток для всіх водіїв
    const [showBatchModal, setShowBatchModal] = useState(false);
    const [batchPreview, setBatchPreview] = useState(null);
    const [batchSelectedDebtIds, setBatchSelectedDebtIds] = useState({}); // { driver_id: number[] } — id боргів, які списувати в рапорті
    const [batchDriverInclude, setBatchDriverInclude] = useState({}); // { driver_id: true/false } — чи створювати рапорт для водія
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchSaving, setBatchSaving] = useState(false);
    const [batchResult, setBatchResult] = useState(null);
    const [showBatchPreviewLoading, setShowBatchPreviewLoading] = useState(false);
    const [batchPreviewPhase, setBatchPreviewPhase] = useState("initial"); // 'initial' | 'per_driver'
    const [batchPreviewStepIndex, setBatchPreviewStepIndex] = useState(0);
    const [batchPreviewApiResult, setBatchPreviewApiResult] = useState(null);
    const [batchPreviewDriverIndex, setBatchPreviewDriverIndex] = useState(0);
    const [batchPreviewDriverStepIndex, setBatchPreviewDriverStepIndex] = useState(0);
    const batchPreviewTimeoutsRef = useRef([]);
    const batchPreviewCancelledRef = useRef(false);
    const batchPreviewApiResultRef = useRef(null);
    const batchPreviewDriverIndexRef = useRef(0);
    const batchPreviewDriverStepIndexRef = useRef(0);

    const [showExistingReportModal, setShowExistingReportModal] = useState(false);
    const [existingReportRow, setExistingReportRow] = useState(null);
    const [existingReportDebts, setExistingReportDebts] = useState([]);
    const [existingReportCheckLoading, setExistingReportCheckLoading] = useState(false);
    const [draftLoadNotice, setDraftLoadNotice] = useState(null);
    const [pdfReportV2Loading, setPdfReportV2Loading] = useState(false);

    const firstRender = useRef(true); // ✅ Оголошуємо useRef всередині функції
    const authFetch = async (url, options = {}) => {
        const token = localStorage.getItem("token");
        if (!token) {
            alert("Сесія завершилась. Увійдіть знову.");
            navigate("/login");
            throw new Error("Missing auth token");
        }

        return fetch(url, {
            ...options,
            headers: {
                ...(options.headers || {}),
                Authorization: `Bearer ${token}`,
            },
        });
    };

    useEffect(() => {
        console.log("🔍 Виконується запит на отримання користувачів...");

        fetch("/api/users")
            .then(response => response.json())
            .then(data => {
                console.log("📋 Отримані користувачі:", data);
                setUsers(data);
            })
            .catch(error => console.error("❌ Помилка завантаження користувачів:", error));
    }, []);
    const location = useLocation();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const driverId = queryParams.get("driverId");
        const week = queryParams.get("week");

        if (driverId && users.length > 0) {
            const user = users.find(u => u.id === parseInt(driverId));
            if (user) {
                setSelectedUser({ value: user.id, label: `${user.firstName} ${user.lastName}` });
                setWeekNumber(Number(week));
            }
        }
    }, [users, location.search]);

    /** Відкриті борги для обраного тижня рапорту: не показуємо в тижні створення боргу та раніше. */
    const debtsEligibleForReportWeek = useMemo(
        () => debts.filter((d) => openDebtVisibleAfterCreationWeek(d, weekNumber)),
        [debts, weekNumber]
    );

    useEffect(() => {
        setSelectedDebts((prev) =>
            prev.filter((id) => debtsEligibleForReportWeek.some((d) => String(d.id) === String(id)))
        );
    }, [weekNumber, debtsEligibleForReportWeek]);

    useEffect(() => {
        if (showDebtModal && debtsEligibleForReportWeek.length === 0) {
            setShowDebtModal(false);
        }
    }, [showDebtModal, debtsEligibleForReportWeek]);

    const normalizeExtraFundsFromSaved = (raw) => {
        if (!raw || typeof raw !== "object") return {};
        let refundsItems = [];
        try {
            refundsItems = typeof raw.refundsDesc === "string" ? JSON.parse(raw.refundsDesc || "[]") : (raw.refundsDesc || []);
            if (!Array.isArray(refundsItems)) refundsItems = [];
        } catch (e) {
            refundsItems = [];
        }
        let otherExpensesItems = [];
        try {
            otherExpensesItems = typeof raw.other_expenses === "string" ? JSON.parse(raw.other_expenses || "[]") : (raw.other_expenses || []);
            if (!Array.isArray(otherExpensesItems)) otherExpensesItems = [];
        } catch (e) {
            otherExpensesItems = [];
        }
        return { ...raw, refundsDesc: JSON.stringify(refundsItems), other_expenses: JSON.stringify(otherExpensesItems) };
    };

    const runFetchReport = async () => {
        if (!weekNumber || !selectedUser) {
            console.warn("⚠️ Не всі дані введені! Номер тижня:", weekNumber, "Користувач:", selectedUser);
            return;
        }

        console.log(selectedUser.label);
        const [firstName, lastName] = selectedUser.label.split(" ");

        setLoading(true);
        setDraftLoadNotice(null);
        try {
            const response = await authFetch("/api/new-raport/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    week_number: weekNumber,
                    first_name: firstName,
                    last_name: lastName,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP помилка! Статус: ${response.status}`);
            }

            const data = await response.json();
            console.log("📊 Отримані дані рапорту:", data);
            console.log("🚖 Uber дані:", data.uber);
            console.log("🚗 Bolt дані:", data.bolt);
            console.log("🚕 FreeNow дані:", data.freenow);

            setReportData(data);
            setPartialSettlement(null);
            setAppliedDebtsSnapshot([]);
            checkDebts(selectedUser.value);
            fetchExtraFunds(selectedUser.value, weekNumber);
        } catch (error) {
            console.error("❌ Помилка під час отримання рапорту:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadSavedDraftFromRow = (row) => {
        const j = row?.report_json;
        if (!j || !j.report) {
            alert(t("driverReportLoadDraftInvalid") || "Некоректні дані збереженого рапорту");
            return;
        }
        setReportData(j.report);
        setExtraFunds(normalizeExtraFundsFromSaved(j.extraFunds || {}));
        setTotalDebt(round2(parseFloat(j.totalDebt) || 0));
        setPartialSettlement(null);
        const snapFromJson = Array.isArray(j.appliedDebtsSnapshot) ? j.appliedDebtsSnapshot : [];
        const debtIdsFromJson = Array.isArray(j.selectedDebts) ? j.selectedDebts : [];
        const tdFromJson = round2(parseFloat(j.totalDebt) || 0);
        if (snapFromJson.length > 0) {
            setAppliedDebtsSnapshot(snapFromJson);
        } else if (debtIdsFromJson.length > 0 && tdFromJson > EPSILON) {
            setAppliedDebtsSnapshot([]);
            (async () => {
                try {
                    const r = await authFetch(`/api/debts/byIds?ids=${debtIdsFromJson.join(",")}`);
                    if (!r.ok) return;
                    const arr = await r.json();
                    let items = debtIdsFromJson.map((idRaw) => {
                        const d = (arr || []).find((x) => String(x.id) === String(idRaw));
                        if (!d) return null;
                        return {
                            id: d.id,
                            title: d.debt_name || `${t("debt")} #${d.id}`,
                            desc: String(d.debt_create_desc || "").trim(),
                            amount: round2(Number(d.debt_total) || 0),
                            week: d.week_number != null && d.week_number !== "" ? d.week_number : null,
                        };
                    }).filter(Boolean);
                    const sum = round2(items.reduce((s, x) => s + x.amount, 0));
                    if (items.length && sum > EPSILON && Math.abs(sum - tdFromJson) > EPSILON) {
                        const k = tdFromJson / sum;
                        items = items.map((x) => ({ ...x, amount: round2(x.amount * k) }));
                    }
                    if (items.length) setAppliedDebtsSnapshot(items);
                } catch (e) {
                    console.warn("debt snapshot rebuild from draft:", e);
                }
            })();
        } else {
            setAppliedDebtsSnapshot([]);
        }
        setSelectedDebts([]);
        setDebts([]);
        setShowDebtModal(false);
        setSalaryInput(parseFloat(j.extraFunds?.sallary) || 0);
        const ef = j.extraFunds || {};
        const hasData = Object.values(ef).some((value) => value !== null && value !== 0 && value !== "" && value !== undefined);
        setHasExtraFunds(hasData);
        setDraftLoadNotice(
            t("driverReportDraftLoadedNotice", {
                defaultValue:
                    "Завантажено збережений рапорт. Суми боргу з чернетки вже враховані; записи боргів у БД могли бути закриті при збереженні — не використовуйте модалку боргів повторно для тих самих id.",
            })
        );
        setShowExistingReportModal(false);
        setExistingReportRow(null);
        setExistingReportDebts([]);
    };

    const handleGenerateClick = async () => {
        if (!weekNumber || !selectedUser) {
            alert(t("chooseWeekAlert") || t("selectWeek") || "Оберіть тиждень та водія");
            return;
        }
        setExistingReportCheckLoading(true);
        try {
            const wk = String(weekNumber);
            const res = await authFetch(
                `/api/admin/report/by-driver-week?driver_id=${selectedUser.value}&week_number=${encodeURIComponent(wk)}`
            );
            if (res.status === 404) {
                await runFetchReport();
                return;
            }
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || `HTTP ${res.status}`);
            }
            const row = await res.json();
            setExistingReportRow(row);
            const ids = row.report_json?.selectedDebts;
            if (Array.isArray(ids) && ids.length > 0) {
                const dr = await authFetch(`/api/debts/byIds?ids=${ids.join(",")}`);
                setExistingReportDebts(dr.ok ? await dr.json() : []);
            } else {
                setExistingReportDebts([]);
            }
            setShowExistingReportModal(true);
        } catch (err) {
            console.error("driverReport existing check:", err);
            alert(t("driverReportExistingCheckError") || "Не вдалося перевірити наявність збереженого рапорту");
        } finally {
            setExistingReportCheckLoading(false);
        }
    };

    const formatReportDateTime = (v) => {
        if (!v) return "—";
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    };

    const fetchExtraFunds = async (driverId, weekNumber) => {
        try {
            const response = await authFetch(`/api/extra-funds-raport?driver_id=${driverId}&week_number=${weekNumber}`);
            if (!response.ok) throw new Error("Помилка отримання extra funds");

            const data = await response.json();
            console.log("📊 Отримані дані extra funds:", data);
            // Ensure refundsDesc is parsed JSON array
            let refundsItems = [];
            try {
                refundsItems = typeof data.refundsDesc === "string" ? JSON.parse(data.refundsDesc || "[]") : (data.refundsDesc || []);
                if (!Array.isArray(refundsItems)) refundsItems = [];
            } catch (e) {
                refundsItems = [];
            }

            // Ensure other_expenses is parsed JSON array
            let otherExpensesItems = [];
            try {
                otherExpensesItems = typeof data.other_expenses === "string" ? JSON.parse(data.other_expenses || "[]") : (data.other_expenses || []);
                if (!Array.isArray(otherExpensesItems)) otherExpensesItems = [];
            } catch (e) {
                otherExpensesItems = [];
            }

            setExtraFunds({ ...data, refundsDesc: JSON.stringify(refundsItems), other_expenses: JSON.stringify(otherExpensesItems) });
            setSalaryInput(parseFloat(data.sallary) || 0); // Синхронізуємо з отриманими даними

            // ✅ Коректна перевірка наявності даних
            const hasData = Object.values(data).some(value => value !== null && value !== 0 && value !== "");

            setHasExtraFunds(hasData);
        } catch (error) {
            console.error("Помилка під час отримання extra funds:", error);
            setHasExtraFunds(false);
        }
    };

    // перевіряємо чи є борги
    const checkDebts = async (driverId) => {
        if (!driverId) {
            console.warn("⚠️ Не передано ID водія для перевірки боргів.");
            return;
        }

        try {
            const response = await fetch(`/api/drivers-debts?driver_id=${driverId}`);
            if (!response.ok) {
                throw new Error(`HTTP помилка! Статус: ${response.status}`);
            }

            const debtsData = await response.json();
            console.log("💳 Отримані борги:", debtsData);

            // ✅ Фільтруємо тільки активні борги (де debt_status = "open")
            const activeDebts = debtsData.filter(debt => debt.debt_status === "open");

            setDebts(activeDebts.map(debt => ({
                ...debt,
                debt_total: Number(debt.debt_total) || 0
            })));

            const eligibleForModal = activeDebts.filter((d) => openDebtVisibleAfterCreationWeek(d, weekNumber));
            if (eligibleForModal.length > 0) {
                setShowDebtModal(true);
            }
        } catch (error) {
            console.error("❌ Помилка при отриманні боргів:", error);
        }
    };
    // робота з боргами у модальному вікні
    const toggleDebtSelection = (debt) => {
        setSelectedDebts((prev) => {
            if (prev.some((x) => String(x) === String(debt.id))) {
                return prev.filter((id) => String(id) !== String(debt.id));
            }
            return [...prev, debt.id];
        });
    };

    const applySelectedDebts = () => {
        if (selectedDebts.length === 0) return;

        const available = round2(Math.max(0, calculatePreDebtPayout()));
        const plan = getDebtAllocationPlan(selectedDebts, debtsEligibleForReportWeek, available);
        const hasUnfunded = selectedDebts.some((id) => plan.roleById[String(id)] === "unfunded");
        if (hasUnfunded || plan.appliedTotal <= EPSILON) return;

        const fullIds = plan.fullCloseIds || [];
        const p = plan.partialDetail;

        setPartialSettlement({
            fullCloseIds: fullIds,
            partial: p
                ? {
                    debtId: p.debtId,
                    amountPaid: p.amountPaid,
                    remainder: p.remainder,
                    debtName: p.debtName,
                    originalTotal: p.originalTotal,
                }
                : null,
        });

        setTotalDebt(plan.appliedTotal);

        const debtSnapshot = [];
        for (const id of fullIds) {
            const d = debts.find((x) => String(x.id) === String(id));
            if (d) {
                debtSnapshot.push({
                    id: d.id,
                    title: d.debt_name || `${t("debt")} #${d.id}`,
                    desc: String(d.debt_create_desc || "").trim(),
                    amount: round2(Number(d.debt_total) || 0),
                    week: d.week_number != null && d.week_number !== "" ? d.week_number : null,
                });
            }
        }
        if (p) {
            const d = debts.find((x) => String(x.id) === String(p.debtId));
            debtSnapshot.push({
                id: p.debtId,
                title: p.debtName || d?.debt_name || `${t("debt")} #${p.debtId}`,
                desc: String(d?.debt_create_desc || "").trim(),
                amount: round2(Number(p.amountPaid) || 0),
                week: d?.week_number != null && d?.week_number !== "" ? d.week_number : null,
            });
        }
        setAppliedDebtsSnapshot(debtSnapshot);

        setFinalData((prev) => {
            const newData = prev.filter((item) => item.name !== "Відняті борги");
            if (plan.appliedTotal > 0) {
                newData.push({ name: "Відняті борги", value: `-${plan.appliedTotal.toFixed(2)}` });
            }
            return newData;
        });

        setSelectedDebts([...fullIds, ...(p ? [p.debtId] : [])]);
        setShowDebtModal(false);
    };

    const handleCloseDebt = async (debtId) => {
        if (!window.confirm("Ви впевнені, що хочете закрити цей борг?")) {
            return;
        }

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`/api/drivers-debts/${debtId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ debt_status: "closed" })
            });

            if (!response.ok) {
                throw new Error(`HTTP помилка! Статус: ${response.status}`);
            }

            setDebts((prevDebts) => {
                const next = prevDebts.filter((debt) => debt.id !== debtId);
                const stillEligible = next.filter((d) => openDebtVisibleAfterCreationWeek(d, weekNumber));
                if (stillEligible.length === 0) {
                    setShowDebtModal(false);
                }
                return next;
            });

            setSelectedDebts((prev) => prev.filter((id) => id !== debtId));

            alert("✅ Борг успішно закрито");
        } catch (error) {
            console.error("❌ Помилка при закритті боргу:", error);
            alert("❌ Не вдалося закрити борг");
        }
    };

    // Глобально доступні податки та загалом сума
    const [taxData, setTaxData] = useState({
        uber: { tax: 0, total: 0 },
        bolt: { tax: 0, total: 0 },
        freenow: { tax: 0, total: 0 }
    });

    // Тут все обрахуємо
    const calculateTaxAndTotal = (data, service) => {
        if (!data || data.length === 0) return { tax: 0, total: 0 };

        let tax = 0;
        let total = 0;

        switch (service) {
            case "uber":
                // Для Uber: Податок 4% від incBrutto
                let incBruttoUber = parseFloat(data[0]?.incBrutto) || 0;
                let incNetto = parseFloat(data[0]?.incNetto) || 0;

                if (incNetto < 0) {
                    total = round2(incNetto);
                    tax = 0;
                } else {
                    tax = round2(incBruttoUber * 0.04);
                    total = round2(incNetto - tax);
                }
                break;

            case "bolt":
                // Для Bolt: Податок 4% від incBruttoBolt, загальний дохід inc_netto - податок
                let incBruttoBolt = parseFloat(data[0]?.incBrutto) || 0;
                let totalInc = parseFloat(data[0]?.totalInc) || 0;
                if (totalInc < 0) {
                    total = round2(totalInc);
                    tax = 0;
                } else {
                    tax = round2(incBruttoBolt * 0.04);
                    total = round2(totalInc - tax);
                }
                break;

            case "freenow":
                // Для FreeNow: Податок 4% від brutto, загальний дохід inc_netto - податок - app_fee
                let brutto = parseFloat(data[0]?.brutto) || 0;
                let saldo_freenow = parseFloat(data[0]?.saldo_freenow) || 0;
                if (saldo_freenow < 0) {
                    total = round2(saldo_freenow);
                    tax = 0;
                } else {
                    tax = round2(brutto * 0.04);
                    total = round2(saldo_freenow - tax);
                }
                break;

            default:
                break;
        }

        return { tax, total };
    };

    // 🔹 Поля, які будемо показувати для кожного сервісу
    const selectedFields = {
        uber: ["incBrutto", "incNetto", "appFee", "appFeeTaxes", "incCash", "tips", "bonus"],
        bolt: ["incBrutto", "totalInc", "incNettoCash", "tips", "bonus"],
        freenow: ["brutto", "saldo_freenow", "cash", "tips", "bonus"]
    };

    // 🔹 Словник для відображення назв колонок у таблиці
    const columnNames = {
        uber: {
            incBrutto: t("brutto"),
            appFee: `Uber ${t("fee").toLocaleLowerCase()}`,
            appFeeTaxes: `Uber ${t("taxes").toLocaleLowerCase()}`,
            incCash: t("cash"),
            tips: t("tips"),
            bonus: t("bonus"),
            incNetto: `Uber ${t("balance").toLocaleLowerCase()}`
        },
        bolt: {
            incBrutto: t("brutto"),
            totalInc: `Bolt ${t("balance").toLocaleLowerCase()}`,
            bonus: t("bonus"),
            incNettoCash: t("cash"),
            tips: t("tips")
        },
        freenow: {
            brutto: t("brutto"),
            cash: t("cash"),
            saldo_freenow: `FreeNow ${t("balance").toLocaleLowerCase()}`,
            tips: t("tips"),
            bonus: t("bonus"),

        },
    };

    // 🔹 Поля, до яких треба додати "-"
    const negativeFields = {
        bolt: ["app_fee", "saldo"],
        extraFunds: ["taxesZUS", "partner", "accountant", "carRent", "other"] // Додаємо extra_funds
    };

    // 🔹 Допоміжні функції для точних розрахунків та порівнянь
    const EPSILON = 0.005; // допуск у 0.5 копійки для уникнення проблем з округленням
    const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

    // Сума, доступна під погашення боргів ДО їх врахування (pre-debt payout)
    const calculatePreDebtPayout = () => {
        const boltTotal = taxData.bolt.total || 0;
        const uberTotal = taxData.uber.total || 0;
        const freenowTotal = taxData.freenow.total || 0;

        const returnVAT = round2(parseFloat(extraFunds.returnVAT) || 0);
        const costs = round2(
            (parseFloat(extraFunds.taxesZUS) || 0) +
            (parseFloat(extraFunds.partner) || 0) +
            (parseFloat(extraFunds.accountant) || 0) +
            (parseFloat(extraFunds.carRent) || 0) +
            (parseFloat(extraFunds.sallary) || 0) +
            (parseFloat(extraFunds.other) || 0) -
            (parseFloat(extraFunds.refunds) || 0)
        );

        return round2(boltTotal + uberTotal + freenowTotal + returnVAT - costs);
    };

    /**
     * План списання для обраних боргів: від меншого до більшого — повні закриття,
     * не більше одного часткового; решта обраних без покриття цією виплатою.
     * roleById: ключ String(debt.id) -> 'full' | 'partial' | 'unfunded'
     */
    const getDebtAllocationPlan = (selectedIds, debtRows, availableRaw) => {
        const available = round2(Math.max(0, availableRaw));
        const idSet = new Set((selectedIds || []).map((x) => String(x)));
        const sortedSelected = (debtRows || [])
            .filter((d) => idSet.has(String(d.id)))
            .slice()
            .sort((a, b) => (Number(a.debt_total) || 0) - (Number(b.debt_total) || 0));

        const roleById = {};
        let remaining = available;

        for (let i = 0; i < sortedSelected.length; i++) {
            const d = sortedSelected[i];
            const idKey = String(d.id);
            const total = round2(Number(d.debt_total) || 0);

            if (remaining <= EPSILON) {
                roleById[idKey] = "unfunded";
                for (let j = i + 1; j < sortedSelected.length; j++) {
                    roleById[String(sortedSelected[j].id)] = "unfunded";
                }
                break;
            }
            if (remaining + EPSILON >= total) {
                roleById[idKey] = "full";
                remaining = round2(remaining - total);
            } else {
                roleById[idKey] = "partial";
                for (let j = i + 1; j < sortedSelected.length; j++) {
                    roleById[String(sortedSelected[j].id)] = "unfunded";
                }
                remaining = 0; // уся доступна сума розподілена (повні + частковий)
                break;
            }
        }

        const fullCloseIds = sortedSelected
            .filter((d) => roleById[String(d.id)] === "full")
            .map((d) => d.id);

        let partialDetail = null;
        let rem = available;
        for (const d of sortedSelected) {
            const role = roleById[String(d.id)];
            if (role === "full") {
                rem = round2(rem - round2(Number(d.debt_total) || 0));
            } else if (role === "partial") {
                const total = round2(Number(d.debt_total) || 0);
                partialDetail = {
                    debtId: d.id,
                    amountPaid: round2(rem),
                    remainder: round2(total - rem),
                    debtName: d.debt_name || "",
                    originalTotal: total,
                };
                break;
            } else {
                break;
            }
        }

        const appliedTotal = round2(available - remaining);

        return {
            roleById,
            appliedTotal,
            available,
            sortedSelected,
            fullCloseIds,
            partialDetail,
        };
    };

    /**
     * Кожен списаний у рапорті борг для PDF: заголовок (назва), опис (debt_create_desc), сума.
     * Якщо деталізації немає — один рядок із totalDebt.
     */
    const getAppliedDebtBreakdown = () => {
        const td = round2(parseFloat(totalDebt) || 0);
        if (td <= EPSILON) return [];

        if (appliedDebtsSnapshot && appliedDebtsSnapshot.length > 0) {
            const mapped = appliedDebtsSnapshot.map((x) => ({
                title: x.title || `${t("debt")} #${x.id ?? ""}`,
                desc: (x.desc && String(x.desc).trim()) || "",
                amount: round2(Number(x.amount) || 0),
                week: x.week != null && x.week !== "" ? x.week : null,
            })).filter((x) => x.amount > EPSILON);
            if (mapped.length) return mapped;
        }

        const out = [];
        const pushFull = (d) => {
            if (!d) return;
            const amt = round2(Number(d.debt_total) || 0);
            if (amt <= EPSILON) return;
            out.push({
                title: d.debt_name || `${t("debt")} #${d.id}`,
                desc: (d.debt_create_desc && String(d.debt_create_desc).trim()) || "",
                amount: amt,
                week: d.week_number != null && d.week_number !== "" ? d.week_number : null,
            });
        };

        const ps = partialSettlement;
        if (ps && ((ps.fullCloseIds && ps.fullCloseIds.length > 0) || ps.partial)) {
            for (const id of ps.fullCloseIds || []) {
                pushFull(debts.find((x) => String(x.id) === String(id)));
            }
            if (ps.partial) {
                const p = ps.partial;
                const d = debts.find((x) => String(x.id) === String(p.debtId));
                const paid = round2(Number(p.amountPaid) || 0);
                if (paid > EPSILON) {
                    out.push({
                        title: p.debtName || d?.debt_name || `${t("debt")} #${p.debtId}`,
                        desc: (d?.debt_create_desc && String(d.debt_create_desc).trim()) || "",
                        amount: paid,
                        week: d?.week_number != null && d?.week_number !== "" ? d.week_number : null,
                    });
                }
            }
            if (out.length) return out;
        }

        if (selectedDebts.length > 0) {
            const plan = getDebtAllocationPlan(selectedDebts, debtsEligibleForReportWeek, round2(Math.max(0, calculatePreDebtPayout())));
            for (const id of plan.fullCloseIds || []) {
                pushFull(debts.find((x) => String(x.id) === String(id)));
            }
            if (plan.partialDetail) {
                const p = plan.partialDetail;
                const d = debts.find((x) => String(x.id) === String(p.debtId));
                const paid = round2(Number(p.amountPaid) || 0);
                if (paid > EPSILON) {
                    out.push({
                        title: p.debtName || d?.debt_name || `${t("debt")} #${p.debtId}`,
                        desc: (d?.debt_create_desc && String(d.debt_create_desc).trim()) || "",
                        amount: paid,
                        week: d?.week_number != null && d?.week_number !== "" ? d.week_number : null,
                    });
                }
            }
            if (out.length) return out;
        }

        return [
            {
                title: t("includedDebts"),
                desc: t("driverReportDebtFallbackDesc", { defaultValue: "списання без деталізації по записах" }),
                amount: td,
            },
        ];
    };

    /** ID боргів, уже відображених у блоці списання цього рапорту — не дублювати в «активні незакриті». */
    const getDebtIdsAccountedInThisReport = () => {
        const s = new Set();
        (appliedDebtsSnapshot || []).forEach((x) => {
            if (x && x.id != null) s.add(String(x.id));
        });
        const ps = partialSettlement;
        if (ps) {
            (ps.fullCloseIds || []).forEach((id) => s.add(String(id)));
            if (ps.partial && ps.partial.debtId != null) s.add(String(ps.partial.debtId));
        }
        return s;
    };

    const PDF_SERVICE_TOTAL_KEYS = ["Uber", "Bolt", "FreeNow"];

    const parseCalculationValueForSort = (valueStr) => {
        if (valueStr == null) return NaN;
        let s = String(valueStr).trim().replace(/\u00a0/g, " ");
        if (s === "" || s === "—" || s === "–") return NaN;
        s = s.replace(/PLN/gi, "").replace(/\s/g, "").replace(",", ".");
        const m = s.match(/-?\d+(?:\.\d+)?/);
        if (!m) return NaN;
        return parseFloat(m[0]);
    };

    /** Порядок рядків у блоці «Загалом»: сервіси → усі від’ємні → усі додатні. */
    const reorderFinalCalculationRows = (rows) => {
        const list = rows || [];
        const isServiceRow = (r) => typeof r.name === "string" && PDF_SERVICE_TOTAL_KEYS.includes(r.name);
        const services = PDF_SERVICE_TOTAL_KEYS.map((key) => list.find((r) => r.name === key)).filter(Boolean);
        const rest = list.filter((r) => !isServiceRow(r));

        const neg = [];
        const pos = [];
        const neutral = [];
        for (const r of rest) {
            const n = parseCalculationValueForSort(r.value);
            if (Number.isNaN(n)) {
                neutral.push(r);
            } else if (Math.abs(n) < EPSILON) {
                /* нулі не показуємо */
            } else if (n < 0) {
                neg.push(r);
            } else {
                pos.push(r);
            }
        }
        return [...services, ...neg, ...pos, ...neutral];
    };

    /** Текст кнопки застосування плану боргів у модалці */
    const formatDebtApplyButtonLabel = (plan, debtRows) => {
        const fullRows = (plan.fullCloseIds || [])
            .map((id) => debtRows.find((d) => String(d.id) === String(id)))
            .filter(Boolean);
        const p = plan.partialDetail;
        const fullParts = fullRows.map(
            (d) => `${d.debt_name || "—"} — ${round2(Number(d.debt_total) || 0).toFixed(2)} PLN`
        );

        if (fullRows.length > 0 && p) {
            return t("debtApplyButtonFullAndPartial", {
                count: fullRows.length,
                fullList: fullParts.join("; "),
                partialName: p.debtName || "—",
                paid: p.amountPaid.toFixed(2),
                remainder: p.remainder.toFixed(2),
                defaultValue:
                    `Закрити ${fullRows.length} ${fullRows.length === 1 ? "борг" : "борги"}: ${fullParts.join("; ")}. Частково з «${p.debtName}» списати ${p.amountPaid.toFixed(2)} PLN (залишиться боргу: ${p.remainder.toFixed(2)} PLN).`,
            });
        }
        if (p && fullRows.length === 0) {
            return t("debtApplyButtonPartialOnly", {
                partialName: p.debtName || "—",
                paid: p.amountPaid.toFixed(2),
                remainder: p.remainder.toFixed(2),
                defaultValue:
                    `Частково з «${p.debtName}» списати ${p.amountPaid.toFixed(2)} PLN (залишиться боргу: ${p.remainder.toFixed(2)} PLN).`,
            });
        }
        if (fullRows.length > 0 && !p) {
            const sum = round2(fullRows.reduce((s, d) => s + (Number(d.debt_total) || 0), 0));
            return t("debtApplyButtonFullOnly", {
                count: fullRows.length,
                fullList: fullParts.join("; "),
                total: sum.toFixed(2),
                defaultValue:
                    `Закрити ${fullRows.length} ${fullRows.length === 1 ? "борг" : "борги"} на ${sum.toFixed(2)} PLN: ${fullParts.join("; ")}.`,
            });
        }
        return `${t("add")} ${t("debt").toLocaleLowerCase()}`;
    };

    const isDebtSelected = (debtId) => selectedDebts.some((x) => String(x) === String(debtId));

    // 💰 Сума до розподілу на ЗП (без поточної зарплати). Може бути від'ємною — тоді ЗП додається до боргу.
    const getTotalBeforeSalary = () => {
        const boltTotal = taxData.bolt.total || 0;
        const uberTotal = taxData.uber.total || 0;
        const freenowTotal = taxData.freenow.total || 0;

        const returnVAT = round2(parseFloat(extraFunds.returnVAT) || 0);
        const costs = round2(
            (parseFloat(extraFunds.taxesZUS) || 0) +
            (parseFloat(extraFunds.partner) || 0) +
            (parseFloat(extraFunds.accountant) || 0) +
            (parseFloat(extraFunds.carRent) || 0) +
            (parseFloat(extraFunds.other) || 0) -
            (parseFloat(extraFunds.refunds) || 0)
        );

        return round2(boltTotal + uberTotal + freenowTotal + returnVAT - costs - parseFloat(totalDebt));
    };

    // 💾 Зберегти зарплату на сервер
    const handleSaveSalary = async () => {
        if (!selectedUser || !weekNumber) {
            alert("⚠️ Виберіть водія та тиждень");
            return;
        }

        const token = localStorage.getItem("token");
        setIsSalaryUpdating(true);

        try {
            const response = await fetch("/api/extra-funds-raport", {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    driver_id: selectedUser.value,
                    week_number: weekNumber,
                    sallary: parseFloat(salaryInput) || 0
                })
            });

            if (!response.ok) {
                throw new Error("Помилка збереження зарплати");
            }

            const result = await response.json();
            
            // Оновлюємо extraFunds
            setExtraFunds(prev => ({
                ...prev,
                sallary: parseFloat(salaryInput) || 0
            }));

            alert(`✅ ${result.message || "Зарплату збережено"}`);
            setShowSalaryDistribution(false); // Закриваємо блок після збереження
        } catch (error) {
            console.error("❌ Помилка збереження зарплати:", error);
            alert("❌ Не вдалося зберегти зарплату");
        } finally {
            setIsSalaryUpdating(false);
        }
    };

    // Приводимо значення до валюти з урахуванням допуску, щоб уникати -0.00 / 0.00 похибок
    const toCurrencyNumber = (value) => {
        const num = Number(value) || 0;
        return Math.abs(num) < EPSILON ? 0 : num;
    };

    // додаємо податки та загалом у таблицю
    useEffect(() => {
        if (reportData) {
            setTaxData({
                uber: calculateTaxAndTotal(reportData.uber, "uber"),
                bolt: calculateTaxAndTotal(reportData.bolt, "bolt"),
                freenow: calculateTaxAndTotal(reportData.freenow, "freenow"),
            });
        }
    }, [reportData]);

    const fieldsForPDF = {
        uber: ["incBrutto", "incNetto", "incCash", "tips", "bonus"],
        bolt: ["incBrutto", "totalInc", "incNettoCash", "tips", "bonus"],
        freenow: ["brutto", "saldo_freenow", "cash", "tips", "bonus"],
        extraFunds: ["returnVAT", "taxesZUS", "partner", "accountant", "carRent", "other", "sallary", "refunds"],
        final: ["PayOut"]
    };

    const customColumnNames = {
        uber: {
            incBrutto: t("brutto"),
            incNetto: `Uber ${t("payment").toLocaleLowerCase()}`,
            appFee: `Uber ${t("fee").toLocaleLowerCase()}`,
            appFeeTaxes: `Uber ${t("taxes").toLocaleLowerCase()}`,
            incCash: t("cash"),
            tips: t("tips"),
            bonus: t("bonus"),
        },
        bolt: {
            incBrutto: t("brutto"),
            totalInc: `Bolt ${t("payment").toLocaleLowerCase()}`,
            bonus: t("bonus"),
            incNettoCash: t("cash"),
            tips: t("tips"),

        },
        freenow: {
            brutto: t("brutto"),
            cash: t("cash"),
            saldo_freenow: `FreeNow ${t("payment").toLocaleLowerCase()}`,
            tips: t("tips"),
            bonus: t("cash"),
        },
        extraFunds: {
            returnVAT: `${t("return")} ${t("vat")}`,
            taxesZUS: `${t("taxes")} ${t("zus")}`,
            partner: `${t("partner")} ${t("fee").toLocaleLowerCase()}`,
            accountant: t("accountant"),
            carRent: t("carRent"),
            other: t("otherCosts"),
            sallary: t("sallary"),
            refunds: t("refunds"),
        },
        final: {
            Debt: `${t("total")} ${t("debt").toLocaleLowerCase()}`,
            PayOut: `${t("total")} ${t("payment").toLocaleLowerCase()}`,
        }
    };

    const downloadPDF = () => {
        if (!selectedUser || !weekNumber) {
            console.warn("⚠️ Виберіть водія та номер тижня перед збереженням PDF.");
            return;
        }

        if (!reportData) {
            console.error("❌ Дані звіту не отримані! Переконайтеся, що ви згенерували звіт перед завантаженням PDF.");
            return;
        }

        const refundsItems = (() => {
            try {
                const parsed = typeof extraFunds.refundsDesc === "string" ? JSON.parse(extraFunds.refundsDesc || "[]") : (extraFunds.refundsDesc || []);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) { return []; }
        })();

        const otherExpensesItems = (() => {
            try {
                const parsed = typeof extraFunds.other_expenses === "string" ? JSON.parse(extraFunds.other_expenses || "[]") : (extraFunds.other_expenses || []);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) { return []; }
        })();

        // 🔹 Оновлюємо `finalData` перед створенням PDF
        const boltTotal = taxData.bolt.total || 0;
        const uberTotal = taxData.uber.total || 0;
        const freenowTotal = taxData.freenow.total || 0;

        const payoutRaw = round2(
            (boltTotal || 0) +
            (uberTotal || 0) +
            (freenowTotal || 0) -
            (parseFloat(totalDebt)) +
            (parseFloat(extraFunds.returnVAT) || 0) -
            ((parseFloat(extraFunds.taxesZUS) || 0) +
                (parseFloat(extraFunds.partner) || 0) +
                (parseFloat(extraFunds.accountant) || 0) +
                (parseFloat(extraFunds.carRent) || 0) +
                (parseFloat(extraFunds.sallary) || 0) +
                (parseFloat(extraFunds.other) || 0) -
                (parseFloat(extraFunds.refunds) || 0))
        );
        const payout = toCurrencyNumber(payoutRaw);


        const otherExpensesRow = {
            name: otherExpensesItems.length > 0
                ? `${t("other")} (${otherExpensesItems.map(e => e.description).join(", ")})`
                : t("other"),
            value: `-${extraFunds.other || 0}`,
            description: otherExpensesItems.length > 0
                ? otherExpensesItems.map(e => e.description).join(", ")
                : "інше / inne / другое"
        };


        const updatedFinalData = reorderFinalCalculationRows([
            { name: "Uber", value: uberTotal.toFixed(2) },
            { name: "Bolt", value: boltTotal.toFixed(2) },
            { name: "FreeNow", value: freenowTotal.toFixed(2) },
            { name: `${t("return")} ${t("vat")}`, value: extraFunds.returnVAT, description: "повернення VAT / zwrot VAT / возврат VAT" },
            { name: `${t("taxes")} ${t("zus")}`, value: extraFunds.taxesZUS ? `-${extraFunds.taxesZUS}` : "0", description: "податки + ЗУС / podatki + ZUS / налоги + ЗУС" },
            { name: t("partner"), value: extraFunds.partner ? `-${extraFunds.partner}` : "0", description: "партнер / partner / партнер" },
            { name: t("accountant"), value: extraFunds.accountant ? `-${extraFunds.accountant}` : "0", description: "бухгалтер / księgowy / бухгалтер" },
            { name: t("carRent"), value: extraFunds.carRent ? `-${extraFunds.carRent}` : "0", description: "оренда авто / wynajem samochodu / аренда авто" },
            ...[otherExpensesRow],
            { 
                name: refundsItems.length > 0
                    ? `${t("refunds")} (${refundsItems.map(e => e.description).join(", ")})`
                    : t("refunds"), 
                value: extraFunds.refunds ? `${extraFunds.refunds}` : "0", 
                description: "повернення / zwrot / возврат" 
            },
            ...getAppliedDebtBreakdown().map((d) => {
                const stackLines = [
                    { text: `${t("debt")}: ${d.title}`, bold: true, margin: [0, 0, 0, 2] },
                ];
                if (d.week != null && d.week !== "") {
                    stackLines.push({ text: `${t("week")} ${d.week}`, fontSize: 8, color: "#444444" });
                }
                stackLines.push({
                    text: d.desc && d.desc.trim() ? d.desc : "—",
                    fontSize: 9,
                    color: "#333333",
                });
                return {
                    namePdf: { stack: stackLines },
                    value: `-${d.amount.toFixed(2)}`,
                    description: "борг / dług / долг",
                };
            }),
            { name: t("sallary"), value: extraFunds.sallary ? `-${round2(Math.abs(parseFloat(extraFunds.sallary) || 0)).toFixed(2)}` : "0", description: "заробітня плата / wynagrodzenie / зп" },
        ]);

        const accountedDebtIdsForPdf = getDebtIdsAccountedInThisReport();
        const activeDebtsForPdf = debtsEligibleForReportWeek
            .filter((debt) => debt.debt_status === "open")
            .filter((debt) => !accountedDebtIdsForPdf.has(String(debt.id)))
            .map((debt) => ({
                name: debt.debt_name || `${t("debt")} #${debt.id}`,
                value: `${round2(debt.debt_total).toFixed(2)} PLN`,
                week: debt.week_number,
                desc: String(debt.debt_create_desc || "").trim(),
            }));

        const salarySummaryAbs = round2(Math.abs(parseFloat(extraFunds.sallary) || 0));
        const renderPayoutSalarySummary = () => ({
            margin: [0, 8, 0, 12],
            table: {
                widths: ["*", 120],
                body: [
                    [
                        { text: `${t("payment")}:`, fontSize: 11, bold: true },
                        { text: `${round2(payout).toFixed(2)} PLN`, alignment: "right", fontSize: 11, bold: true, color: "#166534" },
                    ],
                    [
                        { text: `${t("sallary")}:`, fontSize: 11, bold: true },
                        { text: `${salarySummaryAbs.toFixed(2)} PLN`, alignment: "right", fontSize: 11, bold: true },
                    ],
                ],
            },
            layout: {
                hLineWidth: () => 0,
                vLineWidth: () => 0,
                paddingLeft: () => 4,
                paddingRight: () => 4,
                paddingTop: () => 3,
                paddingBottom: () => 3,
            },
        });

        console.log("🔹 Оновлені фінальні дані для PDF:", updatedFinalData);
        const convertJsxToText = (jsxElement) => {
            if (!jsxElement) return "";

            if (typeof jsxElement === "string") return jsxElement;

            if (Array.isArray(jsxElement)) {
                return jsxElement.map(convertJsxToText).join(" ");
            }

            if (jsxElement.props && jsxElement.props.children) {
                return convertJsxToText(jsxElement.props.children);
            }

            return "";
        };

        // Використовуємо `updatedFinalData` у `renderTotal()`
        const renderTotal = () => {
            return {
                table: {
                    headerRows: 1,
                    widths: ["80%", "20%"], // 2 колонки: "Назва (опис)" | "Значення"
                    body: [
                        [{ text: `${t("total")} ${t("calculation").toLocaleLowerCase()}`, style: "tableHeader", colSpan: 2, alignment: "center" }, {}],
                        ...updatedFinalData.flatMap((row) => {
                            const nameText = row.namePdf
                                ? ""
                                : Array.isArray(row.name)
                                    ? row.name.join("\n")
                                    : convertJsxToText(row.name).trim();
                            const isSalaryHighlight = row.name === t("sallary");

                            const leftCell = row.namePdf
                                ? {
                                    ...row.namePdf,
                                    fillColor: isSalaryHighlight ? "#FFA500" : undefined,
                                }
                                : {
                                    text: nameText,
                                    fillColor: isSalaryHighlight ? "#FFA500" : undefined,
                                    color: isSalaryHighlight ? "black" : undefined,
                                    bold: isSalaryHighlight,
                                    fontSize: isSalaryHighlight ? 14 : undefined,
                                };

                            return [
                                [
                                    leftCell,
                                    {
                                        text: row.value.toString(),
                                        alignment: "right",
                                        fillColor: isSalaryHighlight ? "#FFA500" : undefined,
                                        color: isSalaryHighlight ? "black" : undefined,
                                        bold: isSalaryHighlight,
                                        fontSize: isSalaryHighlight ? 14 : undefined,
                                    },
                                ],
                            ];
                        }),


                    ],
                },
                style: "table",
            };
        };

        const renderActiveDebtsBlock = () => {
            if (activeDebtsForPdf.length === 0) {
                return { text: "" };
            }

            return {
                margin: [0, 10, 0, 0],
                table: {
                    headerRows: 1,
                    widths: ["80%", "20%"],
                    body: [
                        [
                            {
                                text: t("activeUnclosedDebtsTitle"),
                                colSpan: 2,
                                alignment: "center",
                                style: "activeDebtHeader"
                            },
                            {}
                        ],
                        ...activeDebtsForPdf.map((debt) => {
                            const headLine = debt.week != null && debt.week !== ""
                                ? `${debt.name} (${t("week")} ${debt.week})`
                                : debt.name;
                            const descText = String(debt.desc || "").trim() || "—";
                            const stackLines = [
                                { text: headLine, bold: true, color: "#b91c1c", fontSize: 11 },
                            ];
                            stackLines.push({
                                text: descText,
                                fontSize: 8,
                                color: "#991b1b",
                                margin: [0, 3, 0, 0],
                            });
                            return [
                                { stack: stackLines },
                                {
                                    text: debt.value,
                                    alignment: "right",
                                    bold: true,
                                    color: "#b91c1c",
                                    fontSize: 11,
                                },
                            ];
                        })
                    ]
                },
                layout: {
                    hLineColor: () => "#dc3545",
                    vLineColor: () => "#dc3545",
                    hLineWidth: () => 1,
                    vLineWidth: () => 1
                }
            };
        };

        const lastName = selectedUser.label.split(" ")[1] || selectedUser;
        const fileName = `raport-${lastName}-${weekNumber}.pdf`;

        const docDefinition = {
            content: [
                { text: `${selectedUser.label}`, style: "header" },
                { text: `${t("week")}: ${getWeekDatesFromFormatted(weekNumber)}`, style: "subheader" },
                "\n",
                renderSection("Uber", reportData.uber, fieldsForPDF.uber, "uber"),
                renderSection("Bolt", reportData.bolt, fieldsForPDF.bolt, "bolt"),
                renderSection("FreeNow", reportData.freenow, fieldsForPDF.freenow, "freenow"),
                renderTotal(), // ✅ Додаємо виплату
                renderPayoutSalarySummary(),
                renderActiveDebtsBlock(),
            ],
            styles: {
                header: { fontSize: 18, bold: true, alignment: "center" },
                subheader: { fontSize: 14, margin: [0, 10, 0, 5] },
                table: { margin: [0, 5, 0, 15] },
                tableHeader: { bold: true, fontSize: 12, color: "black" },
                activeDebtHeader: { bold: true, fontSize: 12, color: "#dc3545", fillColor: "#fdeaea" },
                activeDebtCell: { color: "#dc3545", bold: true },
            },
        };
        console.log(extraFunds.other_expenses);
        pdfMake.createPdf(docDefinition).download(fileName);
    };

    const renderSection = (title, data, fields, service) => {
        if (!data || data.length === 0) return { text: "" };

        // Отримуємо значення податку та загальної суми
        const tax = taxData[service]?.tax?.toFixed(2) || "0";
        const total = taxData[service]?.total?.toFixed(2) || "0";

        return {
            table: {
                headerRows: 1,
                widths: ["80%", "20%"], // Однакові ширини колонок
                body: [
                    [{ text: title, style: "tableHeader", colSpan: 2, alignment: "center" }, {}],
                    ...fields.map((key) => {
                        if (data[0] && data[0][key] !== undefined) {
                            const value = data[0][key] !== null ? data[0][key].toString() : "—";
                            const columnInfo = customColumnNames[service]?.[key] || {}; // Отримуємо назву

                            return [
                                {
                                    text: columnInfo.name || customColumnNames[service]?.[key] || key
                                }, // Назва + Опис у дужках
                                { text: value, alignment: "right" } // Значення вирівняне вправо
                            ];
                        }
                        return null;
                    }).filter(Boolean),

                    // 🔹 Додаємо Податок 4%
                    [{ text: `${t("taxes")} VAT  4%`, bold: true }, { text: `-${tax}`, alignment: "right" }],

                    // 🔹 Додаємо підсумкову суму
                    [{ text: t("total"), bold: true }, { text: total, alignment: "right" }],
                ],
            },
            style: "table",
        };
    };
    const handleSaveDraft = async () => {
        if (!selectedUser || !weekNumber || !reportData) {
            alert("⚠️ Спочатку згенеруй рапорт");
            return;
        }

        // Рахуємо суму виплати
        const payoutRaw = round2(
            (taxData.uber.total || 0) +
            (taxData.bolt.total || 0) +
            (taxData.freenow.total || 0) -
            (parseFloat(totalDebt)) +
            (parseFloat(extraFunds.returnVAT) || 0) -
            (
                (parseFloat(extraFunds.taxesZUS) || 0) +
                (parseFloat(extraFunds.partner) || 0) +
                (parseFloat(extraFunds.accountant) || 0) +
                (parseFloat(extraFunds.carRent) || 0) +
                (parseFloat(extraFunds.sallary) || 0) +
                (parseFloat(extraFunds.other) || 0) -
                (parseFloat(extraFunds.refunds) || 0)
            )
        );
        const payout = toCurrencyNumber(payoutRaw);

        const token = localStorage.getItem("token"); // 🔐 Отримуємо токен

        // План з модалки: одне або кілька повних закриттів + не більше одного часткового (новий борг на залишок)
        const savedPlan = partialSettlement;
        const hasStructuredPlan =
            savedPlan &&
            ((savedPlan.fullCloseIds && savedPlan.fullCloseIds.length > 0) || savedPlan.partial);

        if (hasStructuredPlan) {
            const fullIds = savedPlan.fullCloseIds || [];
            const p = savedPlan.partial;
            const debtsToClose = [...fullIds, ...(p ? [p.debtId] : [])];

            const payload = {
                driver_id: selectedUser.value,
                week_number: weekNumber,
                report_data: {
                    report: reportData,
                    extraFunds,
                    selectedDebts: debtsToClose,
                    appliedDebtsSnapshot,
                    totalDebt,
                    taxData,
                    payout: payout.toFixed(2)
                }
            };

            try {
                const response = await fetch("/api/save-report", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    const error = await response.json();
                    console.error("❌", error);
                    alert("❌ Не вдалося зберегти рапорт");
                    return;
                }

                if (p && p.remainder > EPSILON) {
                    const createDebtBody = {
                        driver_id: selectedUser.value,
                        week_number: weekNumber,
                        year: new Date().getFullYear(),
                        debt_total: round2(p.remainder),
                        debt_name: p.debtName || `Борг за тиждень ${weekNumber}`,
                        debt_create_desc:
                            `Часткове списання «${p.debtName || "борг"}»: ${p.amountPaid.toFixed(2)} PLN, залишок ${p.remainder.toFixed(2)} PLN.${fullIds.length ? ` Повністю закрито записів: ${fullIds.length}.` : ""} Тиждень ${weekNumber}.`,
                        debt_status: "open"
                    };

                    const createRes = await fetch("/api/drivers-debts", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify(createDebtBody)
                    });

                    if (!createRes.ok) {
                        const err = await createRes.json();
                        console.error("❌ Помилка створення нового боргу:", err);
                        alert("✅ Рапорт збережено, але не вдалося створити новий борг на залишок");
                    } else {
                        alert("✅ Рапорт збережено. Створено новий борг на залишок");
                    }

                    await checkDebts(selectedUser.value);
                } else {
                    alert("✅ Рапорт збережено як чернетку");
                }

                setPartialSettlement(null);
                return;
            } catch (error) {
                console.error("❌", error);
                alert("❌ Сталася помилка при збереженні");
                return;
            }
        }

        // Звичайний сценарій: закриття повних боргів
        const selectedDebtsData = debts.filter((debt) => selectedDebts.some((x) => String(x) === String(debt.id)));
        let remainingPayout = payout;

        const debtsToClose = selectedDebtsData
            .filter(debt => {
                if (remainingPayout >= debt.debt_total) {
                    remainingPayout -= debt.debt_total;
                    return true; // можна закрити борг
                }
                return false; // не закриваємо
            })
            .map(debt => debt.id);

        const payload = {
            driver_id: selectedUser.value,
            week_number: weekNumber,
            report_data: {
                report: reportData,
                extraFunds,
                selectedDebts: debtsToClose,
                appliedDebtsSnapshot,
                totalDebt,
                taxData,
                payout: payout.toFixed(2)
            }
        };

        try {
            const response = await fetch("/api/save-report", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                alert(result.message || "✅ Рапорт збережено як чернетку");
            } else {
                const error = await response.json();
                console.error("❌", error);
                alert("❌ Не вдалося зберегти рапорт");
            }
        } catch (error) {
            console.error("❌", error);
            alert("❌ Сталася помилка при збереженні");
        }
    };

    const applyBatchPreviewResult = (data) => {
        if (!data) return;
        const debtIdsInitial = {};
        const includeInitial = {};
        (data.drivers || []).forEach((d) => {
            includeInitial[d.driver_id] = true;
            debtIdsInitial[d.driver_id] = (d.open_debts || []).map((x) => x.id);
        });
        setBatchSelectedDebtIds(debtIdsInitial);
        setBatchDriverInclude(includeInitial);
        setBatchPreview(data);
        setShowBatchModal(true);
        setShowBatchPreviewLoading(false);
        setBatchPreviewApiResult(null);
        setBatchPreviewPhase("initial");
        setBatchPreviewStepIndex(0);
        setBatchPreviewDriverIndex(0);
        setBatchPreviewDriverStepIndex(0);
        batchPreviewApiResultRef.current = null;
        batchPreviewTimeoutsRef.current = [];
    };

    useEffect(() => {
        return () => {
            batchPreviewTimeoutsRef.current.forEach((id) => clearTimeout(id));
            batchPreviewTimeoutsRef.current = [];
        };
    }, []);

    // Batch: превʼю — показуємо модалку завантаження з кроками, паралельно робимо запит
    const fetchBatchPreview = () => {
        if (!weekNumber) {
            alert("⚠️ " + (t("chooseWeekAlert") || "Оберіть тиждень"));
            return;
        }
        setBatchResult(null);
        batchPreviewCancelledRef.current = false;
        batchPreviewTimeoutsRef.current = [];
        setBatchPreviewApiResult(null);
        setBatchPreviewPhase("initial");
        setBatchPreviewStepIndex(0);
        setBatchPreviewDriverIndex(0);
        setBatchPreviewDriverStepIndex(0);
        batchPreviewApiResultRef.current = null;
        batchPreviewDriverIndexRef.current = 0;
        batchPreviewDriverStepIndexRef.current = 0;
        setShowBatchPreviewLoading(true);
        setBatchLoading(true);

        authFetch(`/api/save-reports-batch/preview?week_number=${weekNumber}`)
            .then(async (res) => {
                if (batchPreviewCancelledRef.current) return;
                if (!res.ok) throw new Error(await res.text());
                return res.json();
            })
            .then((data) => {
                if (batchPreviewCancelledRef.current) return;
                if (!data.drivers || data.drivers.length === 0) {
                    setShowBatchPreviewLoading(false);
                    batchPreviewCancelledRef.current = true;
                    alert(t("batchNoDrivers") || "Немає водіїв (роль user) з додатковими коштами та даними з сервісів за цей тиждень.");
                    return;
                }
                batchPreviewApiResultRef.current = data;
                setBatchPreviewApiResult(data);
                setBatchPreviewPhase("per_driver");
                batchPreviewDriverIndexRef.current = 0;
                batchPreviewDriverStepIndexRef.current = 0;
                setBatchPreviewDriverIndex(0);
                setBatchPreviewDriverStepIndex(0);

                const drivers = data.drivers || [];
                const scheduleNextDriverStep = () => {
                    if (batchPreviewCancelledRef.current) return;
                    const di = batchPreviewDriverIndexRef.current;
                    const si = batchPreviewDriverStepIndexRef.current;
                    const delay = 100 + Math.random() * 400;
                    const id = setTimeout(() => {
                        if (batchPreviewCancelledRef.current) return;
                        if (si < BATCH_PREVIEW_DRIVER_STEPS.length - 1) {
                            batchPreviewDriverStepIndexRef.current = si + 1;
                            setBatchPreviewDriverStepIndex(si + 1);
                            scheduleNextDriverStep();
                        } else {
                            if (di + 1 >= drivers.length) {
                                applyBatchPreviewResult(batchPreviewApiResultRef.current);
                                return;
                            }
                            batchPreviewDriverIndexRef.current = di + 1;
                            batchPreviewDriverStepIndexRef.current = 0;
                            setBatchPreviewDriverIndex(di + 1);
                            setBatchPreviewDriverStepIndex(0);
                            scheduleNextDriverStep();
                        }
                    }, delay);
                    batchPreviewTimeoutsRef.current.push(id);
                };
                scheduleNextDriverStep();
            })
            .catch((e) => {
                console.error(e);
                setShowBatchPreviewLoading(false);
                batchPreviewCancelledRef.current = true;
                alert(t("fetchReportError") || "Помилка завантаження превʼю");
            })
            .finally(() => {
                setBatchLoading(false);
            });

        const delay = 100 + Math.random() * 400;
        const id0 = setTimeout(() => {
            if (batchPreviewCancelledRef.current) return;
            setBatchPreviewStepIndex(1);
        }, delay);
        batchPreviewTimeoutsRef.current.push(id0);
    };

    // Batch: створити чернетки (з опційним списанням боргів та вибором водіїв)
    const handleBatchCreate = async () => {
        if (!weekNumber || !batchPreview) return;
        const includeDriverIds = (batchPreview.drivers || []).filter((d) => batchDriverInclude[d.driver_id] !== false).map((d) => d.driver_id);
        if (includeDriverIds.length === 0) {
            alert(t("batchSelectAtLeastOne") || "Оберіть хоча б одного водія для створення рапорту.");
            return;
        }
        setBatchSaving(true);
        setBatchResult(null);
        try {
            const debt_actions = {};
            (batchPreview.drivers || []).forEach((d) => {
                const selectedIds = batchSelectedDebtIds[d.driver_id] || [];
                if (selectedIds.length > 0 && d.open_debts && d.open_debts.length > 0) {
                    const total = d.open_debts.filter((x) => selectedIds.includes(x.id)).reduce((s, x) => s + (x.debt_total || 0), 0);
                    debt_actions[String(d.driver_id)] = {
                        selected_debt_ids: selectedIds,
                        total_debt: Math.round(total * 100) / 100
                    };
                }
            });
            const res = await authFetch("/api/save-reports-batch", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ week_number: weekNumber, debt_actions, include_driver_ids: includeDriverIds })
            });
            if (!res.ok) throw new Error(await res.text());
            const result = await res.json();
            setBatchResult(result);
            if (result.errors && result.errors.length > 0) {
                console.warn("Batch errors:", result.errors);
            }
            const total = (result.created || 0) + (result.updated || 0);
            if (total > 0) {
                setShowBatchModal(false);
                setBatchPreview(null);
                const msg = t("batchSuccess", { created: result.created ?? 0, updated: result.updated ?? 0, closed: result.debt_closed_count ?? 0 });
                const debtCreated = result.debts_created ?? 0;
                alert(debtCreated > 0 ? `${msg} ${t("batchDebtsCreated") || "Створено боргів"}: ${debtCreated}.` : msg);
            }
        } catch (e) {
            console.error(e);
            alert(t("fetchReportError") || "Помилка при створенні чернеток");
        } finally {
            setBatchSaving(false);
        }
    };

    // Функція для отримання дат тижня з формату YYYYWW
    const getWeekDatesFromFormatted = (formattedWeekNumber) => {
        if (!formattedWeekNumber) return "";

        // Конвертуємо в рядок, якщо передано число
        const weekStr = String(formattedWeekNumber);
        
        // Перевіряємо, чи має рядок правильну довжину (6 символів для YYYYWW)
        if (weekStr.length !== 6) {
            console.warn("⚠️ Неправильний формат тижня:", formattedWeekNumber);
            return "";
        }

        // Парсимо формат "YYYYWW" (наприклад, "202538")
        const year = parseInt(weekStr.substring(0, 4));
        const week = parseInt(weekStr.substring(4, 6));

        // Знаходимо перший понеділок року
        let firstMonday = new Date(year, 0, 1);
        while (firstMonday.getDay() !== 1) { // 1 - це понеділок
            firstMonday.setDate(firstMonday.getDate() + 1);
        }

        // Розраховуємо початок тижня
        let weekStart = new Date(firstMonday);
        weekStart.setDate(firstMonday.getDate() + (week - 1) * 7);

        let weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        // Форматуємо дати як в weekSelector.js
        const formatDate = (date) => {
            return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}`;
        };

        return `${formatDate(weekStart)} – ${formatDate(weekEnd)}`;
    };

    /** PDF через HTML + Puppeteer (окремо від pdfMake). Файл: raport-v2-… */
    const downloadPDFReportV2 = async () => {
        if (!selectedUser || !weekNumber) {
            console.warn("⚠️ Виберіть водія та номер тижня перед збереженням PDF.");
            return;
        }
        if (!reportData) {
            console.error("❌ Дані звіту не отримані! Переконайтеся, що ви згенерували звіт перед завантаженням PDF.");
            return;
        }

        setPdfReportV2Loading(true);
        try {
        const refundsItems = (() => {
            try {
                const parsed = typeof extraFunds.refundsDesc === "string" ? JSON.parse(extraFunds.refundsDesc || "[]") : (extraFunds.refundsDesc || []);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) { return []; }
        })();

        const otherExpensesItems = (() => {
            try {
                const parsed = typeof extraFunds.other_expenses === "string" ? JSON.parse(extraFunds.other_expenses || "[]") : (extraFunds.other_expenses || []);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) { return []; }
        })();

        const boltTotal = taxData.bolt.total || 0;
        const uberTotal = taxData.uber.total || 0;
        const freenowTotal = taxData.freenow.total || 0;

        const payoutRaw = round2(
            (boltTotal || 0) +
            (uberTotal || 0) +
            (freenowTotal || 0) -
            (parseFloat(totalDebt)) +
            (parseFloat(extraFunds.returnVAT) || 0) -
            ((parseFloat(extraFunds.taxesZUS) || 0) +
                (parseFloat(extraFunds.partner) || 0) +
                (parseFloat(extraFunds.accountant) || 0) +
                (parseFloat(extraFunds.carRent) || 0) +
                (parseFloat(extraFunds.sallary) || 0) +
                (parseFloat(extraFunds.other) || 0) -
                (parseFloat(extraFunds.refunds) || 0))
        );
        const payout = toCurrencyNumber(payoutRaw);

        const otherExpensesRow = {
            name: otherExpensesItems.length > 0
                ? `${t("other")} (${otherExpensesItems.map(e => e.description).join(", ")})`
                : t("other"),
            value: `-${extraFunds.other || 0}`,
        };

        const updatedFinalData = reorderFinalCalculationRows([
            { name: "Uber", value: uberTotal.toFixed(2) },
            { name: "Bolt", value: boltTotal.toFixed(2) },
            { name: "FreeNow", value: freenowTotal.toFixed(2) },
            { name: `${t("return")} ${t("vat")}`, value: extraFunds.returnVAT },
            { name: `${t("taxes")} ${t("zus")}`, value: extraFunds.taxesZUS ? `-${extraFunds.taxesZUS}` : "0" },
            { name: t("partner"), value: extraFunds.partner ? `-${extraFunds.partner}` : "0" },
            { name: t("accountant"), value: extraFunds.accountant ? `-${extraFunds.accountant}` : "0" },
            { name: t("carRent"), value: extraFunds.carRent ? `-${extraFunds.carRent}` : "0" },
            otherExpensesRow,
            {
                name: refundsItems.length > 0
                    ? `${t("refunds")} (${refundsItems.map(e => e.description).join(", ")})`
                    : t("refunds"),
                value: extraFunds.refunds ? `${extraFunds.refunds}` : "0",
            },
            ...getAppliedDebtBreakdown().map((d) => ({
                debtCalc: true,
                debtLabelLine: `${t("debt")}: ${d.title}`,
                debtWeek:
                    d.week != null && d.week !== ""
                        ? `${t("week")} ${d.week}`
                        : "",
                debtDesc: d.desc && d.desc.trim() ? d.desc : "—",
                value: `-${d.amount.toFixed(2)}`,
            })),
            { name: t("sallary"), value: extraFunds.sallary ? `-${round2(Math.abs(parseFloat(extraFunds.sallary) || 0)).toFixed(2)}` : "0" },
        ]);

        const accountedDebtIdsForPdfV2 = getDebtIdsAccountedInThisReport();
        const activeDebtsForPdf = debtsEligibleForReportWeek
            .filter((debt) => debt.debt_status === "open")
            .filter((debt) => !accountedDebtIdsForPdfV2.has(String(debt.id)))
            .map((debt) => ({
                name: debt.debt_name || `${t("debt")} #${debt.id}`,
                value: `${round2(debt.debt_total).toFixed(2)} PLN`,
                week: debt.week_number,
                desc: String(debt.debt_create_desc || "").trim(),
            }));

        const salarySummaryAbsV2 = round2(Math.abs(parseFloat(extraFunds.sallary) || 0));

        const buildHtmlService = (title, data, fields, serviceKey) => {
            if (!data || data.length === 0) return null;
            const tax = taxData[serviceKey]?.tax?.toFixed(2) || "0";
            const total = taxData[serviceKey]?.total?.toFixed(2) || "0";
            const rows = fields.map((key) => {
                if (data[0] && data[0][key] !== undefined) {
                    const value = data[0][key] != null ? data[0][key].toString() : "—";
                    const label = customColumnNames[serviceKey]?.[key] || key;
                    return { label, value };
                }
                return null;
            }).filter(Boolean);
            return {
                serviceKey,
                title,
                rows,
                taxLabel: `${t("taxes")} VAT 4%`,
                taxValue: `-${tax}`,
                totalLabel: t("total"),
                totalValue: total,
            };
        };

        const services = [
            buildHtmlService("Uber", reportData.uber, fieldsForPDF.uber, "uber"),
            buildHtmlService("Bolt", reportData.bolt, fieldsForPDF.bolt, "bolt"),
            buildHtmlService("FreeNow", reportData.freenow, fieldsForPDF.freenow, "freenow"),
        ].filter(Boolean);

        const rowNameToLabel = (row) => {
            if (Array.isArray(row.name)) return row.name.join("\n");
            if (typeof row.name === "string") return row.name;
            return String(row.name ?? "");
        };

        const calculationRows = updatedFinalData.map((row) => {
            if (row.debtCalc) {
                return {
                    debtCalc: true,
                    debtLabelLine: row.debtLabelLine,
                    debtWeek: row.debtWeek || "",
                    debtDesc: row.debtDesc,
                    value: row.value.toString(),
                };
            }
            return {
                label: rowNameToLabel(row),
                value: row.value.toString(),
            };
        });

        const debtRows = activeDebtsForPdf.map((d) => ({
            openDebt: true,
            titleLine: d.week != null && d.week !== "" ? `${d.name} (${t("week")} ${d.week})` : d.name,
            descLine: d.desc && d.desc.trim() ? d.desc : "—",
            value: d.value,
        }));

        const weekLine = `${t("week")}: ${getWeekDatesFromFormatted(weekNumber)}`;
        const html = buildDriverWeeklyReportPdfHtml({
            driverLabel: selectedUser.label,
            weekLine,
            services,
            calculationTitle: `${t("total")} ${t("calculation").toLocaleLowerCase()}`,
            calculationRows,
            calcSummary: {
                payoutLabel: t("payment"),
                payoutValue: round2(payout).toFixed(2),
                salaryLabel: t("sallary"),
                salaryValue: salarySummaryAbsV2.toFixed(2),
            },
            debtsTitle: t("activeUnclosedDebtsTitle"),
            debtRows,
            footerLine: `PDF 2.0 · ${new Date().toLocaleString()}`,
        });

        const lastName = selectedUser.label.split(" ")[1] || selectedUser.label;
        const fileName = `raport-v2-${lastName}-${weekNumber}.pdf`;

        const res = await authFetch("/api/invoices/generate-from-html", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ html, fileName }),
        });
        if (!res.ok) {
            let msg = "Не вдалося згенерувати PDF";
            try {
                const j = await res.json();
                msg = j.error || j.details || msg;
            } catch (_) { /* ignore */ }
            alert(msg);
            return;
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            alert("Помилка завантаження PDF 2.0");
        } finally {
            setPdfReportV2Loading(false);
        }
    };

    // Функція для отримання дат тижня (legacy для веб-інтерфейсу)
    const getWeekDates = (weekNumber) => {
        if (!weekNumber) return "";

        const currentYear = new Date().getFullYear();

        // Знаходимо перший понеділок поточного року
        let firstDayOfYear = new Date(currentYear, 0, 1);
        while (firstDayOfYear.getDay() !== 1) { // 1 - це понеділок
            firstDayOfYear.setDate(firstDayOfYear.getDate() + 1);
        }

        // Якщо запитаний тиждень - останній тиждень минулого року
        if (weekNumber === 0) {
            let lastYear = currentYear - 1;
            let lastYearLastWeekStart = new Date(lastYear, 11, 31); // 31 грудня минулого року

            while (lastYearLastWeekStart.getDay() !== 1) { // Шукаємо останній понеділок
                lastYearLastWeekStart.setDate(lastYearLastWeekStart.getDate() - 1);
            }

            let lastYearLastWeekEnd = new Date(lastYearLastWeekStart);
            lastYearLastWeekEnd.setDate(lastYearLastWeekStart.getDate() + 6);

            return `${lastYearLastWeekStart.toLocaleDateString("uk-UA")} - ${lastYearLastWeekEnd.toLocaleDateString("uk-UA")}`;
        }

        // Розраховуємо початок тижня
        let weekStart = new Date(firstDayOfYear);
        weekStart.setDate(firstDayOfYear.getDate() + (weekNumber - 1) * 7);

        let weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        return `${weekStart.toLocaleDateString("uk-UA")} - ${weekEnd.toLocaleDateString("uk-UA")}`;
    };


    const reportDates = getWeekDates(weekNumber);

    // Функція для рендерингу 2-колонкової таблиці
    const renderTable = (title, data, service) => {
        if (!data || data.length === 0) return null; // ❌ Не рендеримо таблицю, якщо даних немає

        let totalInc = parseFloat(data[0]?.inc_netto) || 0;
        let tax = totalInc * 0.04;
        let finalAmount = totalInc - tax;

        const columnDescriptions = {
            incBrutto: t("brutto"),
            brutto: t("brutto"),
            appFee: t("fee"),
            appFeeTaxes: t("taxes"),
            incCash: t("cash"),
            tips: t("tips"),
            bonus: t("bonus"),
            cash: t("cash"),
            sallary: t("sallary"),
            inc_netto: t("netProfit"),
            app_fee: `${t("total")} ${t("service").toLocaleLowerCase()} ${t("fee").toLocaleLowerCase()} `,
            saldo_freenow: t("balance"),
            totalInc: t("balance"),
            incNettoCash: t("cash"),
            incNetto: t("balance"),
        };




        return (
            <div className="mt-4 text-center">
                <h3 className="text-primary">{title}</h3>
                <table className="table table-bordered text-center" style={{ margin: "auto" }}>
                    <tbody>
                        {selectedFields[service].map((key, index) => {
                            if (!(key in data[0])) return null; // Якщо поля немає, не додаємо в таблицю

                            let value = data[0][key];
                            if (negativeFields[service]?.includes(key) && value > 0) {
                                value = `-${value}`;
                            }

                            return (
                                <tr key={index}>
                                    <td className="fw-bold" style={{ width: "70%" }}>{columnNames[service][key] || key}

                                    </td>
                                    <td style={{ width: "30%" }}>{value}</td>
                                </tr>
                            );
                        })}

                        {/* 🔹 Додаємо Податок 4% та Загалом тільки для Bolt */}
                        {service === "uber" && (
                            <>
                                <tr>
                                    <td className="fw-bold">{t("taxes")} VAT 4%
                                    </td>
                                    <td>-{taxData.uber.tax.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold">{t("total")}

                                    </td>
                                    <td>{taxData.uber.total.toFixed(2)}</td>
                                </tr>
                            </>
                        )}

                        {service === "bolt" && (
                            <>
                                <tr>
                                    <td className="fw-bold">{t("taxes")} VAT 4%

                                    </td>
                                    <td>-{taxData.bolt.tax.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold">{t("total")}

                                    </td>
                                    <td>{taxData.bolt.total.toFixed(2)}</td>
                                </tr>
                            </>
                        )}

                        {service === "freenow" && (
                            <>
                                <tr>
                                    <td className="fw-bold">{t("taxes")} VAT 4%</td>
                                    <td>-{taxData.freenow.tax.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td className="fw-bold">{t("total")}</td>
                                    <td>{taxData.freenow.total.toFixed(2)}</td>
                                </tr>
                            </>
                        )}

                    </tbody>
                </table>
            </div>
        );
    };

    const renderFinalTable = () => {
        if (!reportData) return null; // ❌ Не рендеримо таблицю, якщо немає даних

        // Парсимо деталі для "Інше" та "Відшкодування"
        const otherExpensesItems = (() => {
            try {
                const parsed = typeof extraFunds.other_expenses === "string" ? JSON.parse(extraFunds.other_expenses || "[]") : (extraFunds.other_expenses || []);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) { return []; }
        })();

        const refundsItems = (() => {
            try {
                const parsed = typeof extraFunds.refundsDesc === "string" ? JSON.parse(extraFunds.refundsDesc || "[]") : (extraFunds.refundsDesc || []);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) { return []; }
        })();

        const boltTotal = taxData.bolt.total || 0;
        const uberTotal = taxData.uber.total || 0;
        const freenowTotal = taxData.freenow.total || 0;

        const payoutRaw = round2(
            (boltTotal || 0) +
            (uberTotal || 0) +
            (freenowTotal || 0) -
            (parseFloat(totalDebt)) +
            (parseFloat(extraFunds.returnVAT) || 0) -
            ((parseFloat(extraFunds.taxesZUS) || 0) +
                (parseFloat(extraFunds.partner) || 0) +
                (parseFloat(extraFunds.accountant) || 0) +
                (parseFloat(extraFunds.carRent) || 0) +
                (parseFloat(extraFunds.sallary) || 0) +
                (parseFloat(extraFunds.other) || 0) -
                (parseFloat(extraFunds.refunds) || 0))
        );
        const payout = toCurrencyNumber(payoutRaw);

        const finalData = [
            { name: "Bolt", value: boltTotal.toFixed(2) },
            { name: "Uber", value: uberTotal.toFixed(2) },
            { name: "FreeNow", value: freenowTotal.toFixed(2) },
            { name: `${t("return")} ${t("vat")}`, value: extraFunds.returnVAT, description: "повернення VAT / zwrot VAT / возврат VAT" },
            { name: `${t("taxes")} ${t("zus")}`, value: extraFunds.taxesZUS ? `-${extraFunds.taxesZUS}` : "0", description: "податки + ЗУС / podatki + ZUS / налоги + ЗУС" },
            { name: t("partner"), value: extraFunds.partner ? `-${extraFunds.partner}` : "0", description: "партнер / partner / партнер" },
            { name: t("accountant"), value: extraFunds.accountant ? `-${extraFunds.accountant}` : "0", description: "бухгалтер / księgowy / бухгалтер" },
            { name: t("carRent"), value: extraFunds.carRent ? `-${extraFunds.carRent}` : "0", description: "оренда авто / wynajem samochodu / аренда авто" },
            { 
                name: otherExpensesItems.length > 0
                    ? `${t("other")} (${otherExpensesItems.map(e => e.description).join(", ")})`
                    : t("other"), 
                value: extraFunds.other ? `-${extraFunds.other}` : "0", 
                description: "інше / inne / другое " 
            },
            { 
                name: refundsItems.length > 0
                    ? `${t("refunds")} (${refundsItems.map(e => e.description).join(", ")})`
                    : t("refunds"), 
                value: extraFunds.refunds ? `${extraFunds.refunds}` : "0", 
                description: "повернення / zwrot / возврат" 
            }, // Refunds НЕ від'ємний
            { name: t("sallary"), value: extraFunds.sallary ? `${extraFunds.sallary}` : "0", description: "заробітня плата / wynagrodzenie / зп" },
            ...(totalDebt > EPSILON
                ? getAppliedDebtBreakdown().map((d) => ({
                    name: `${t("debt")}: ${d.title}`,
                    debtWeekLine:
                        d.week != null && d.week !== ""
                            ? `${t("week")} ${d.week}`
                            : null,
                    debtDesc: d.desc && String(d.desc).trim() ? String(d.desc).trim() : "—",
                    value: `-${d.amount.toFixed(2)}`,
                    description: "борг / dług / долг",
                }))
                : []),
            { name: t("payment"), value: payout.toFixed(2), description: "на руки" },
        ];

        return (
            <div className="mt-4 text-center">
                <h3 className="text-danger">{t("total")} {t("calculation").toLocaleLowerCase()}</h3>
                <table className="table table-bordered text-center" style={{ margin: "auto" }}>
                    <tbody>
                        {finalData.map((row, index) => (
                            <tr key={index}>
                                <td className="fw-bold" style={{ width: "70%" }}>
                                    <div>{row.name}</div>
                                    {row.debtWeekLine && (
                                        <div className="small text-muted fw-normal mt-1">{row.debtWeekLine}</div>
                                    )}
                                    {row.debtDesc != null && (
                                        <div
                                            className="small text-muted fw-normal mt-1 text-start d-inline-block w-100"
                                            style={{ whiteSpace: "pre-wrap" }}
                                        >
                                            {row.debtDesc}
                                        </div>
                                    )}
                                </td>
                                <td style={{ width: "30%" }}>
                                    {row.name === t("sallary") ? (
                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                            <span>{row.value}</span>
                                            <button 
                                                className="btn btn-sm btn-success"
                                                onClick={() => setShowSalaryDistribution(!showSalaryDistribution)}
                                                title={showSalaryDistribution ? "Сховати розподіл" : "Розподілити виплату"}
                                                style={{ fontSize: "1rem", padding: "0.1rem 0.5rem" }}
                                            >
                                                {showSalaryDistribution ? "−" : "+"}
                                            </button>
                                        </div>
                                    ) : (
                                        row.value
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {payout < 0 && selectedUser && (
                    <div className="mt-3">
                        <a
                            href={`/admin/driver-debts?openModal=true&driverId=${selectedUser.value}&week=${weekNumber}&sum=${Math.abs(payout.toFixed(2))}&name=${encodeURIComponent(`Борг за тиждень ${weekNumber}`)}&desc=${encodeURIComponent(`Автоматичний борг, сума виплати за тиждень ${weekNumber} = ${payout.toFixed(2)} PLN`)}`}
                            className="btn btn-danger"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            🔗 {t("create")} {t("debt").toLocaleLowerCase()} {Math.abs(payout.toFixed(2))} PLN
                        </a>
                    </div>
                )}

            </div>

        );

    };

    return (
        <div className="container mt-4">
            <h2 className="mb-4 text-center">{t("driver")} {t("raport").toLocaleLowerCase()}</h2>

            <div className="row mb-3">
                <WeekSelector selectedWeek={weekNumber} setSelectedWeek={setWeekNumber} />

                <div className="col-md-3">
                    <Form.Group className="mb-3">

                        <DriverSelector selectedDriver={selectedUser} setSelectedDriver={setSelectedUser} />
                    </Form.Group>
                </div>
                <div className="col-md-3">
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleGenerateClick}
                        disabled={loading || existingReportCheckLoading}
                    >
                        {existingReportCheckLoading || loading ? "⏳ …" : `${t("generate")} ${t("raport").toLocaleLowerCase()}`}
                    </button>
                </div>
                {weekNumber && (
                    <div className="col-md-3">
                        <button
                            type="button"
                            className="btn btn-outline-primary w-100"
                            onClick={fetchBatchPreview}
                            disabled={batchLoading}
                        >
                            {batchLoading ? "⏳..." : (t("batchCreateDrafts") || "Створити чернетки для всіх")}
                        </button>
                    </div>
                )}
            </div>

            {loading && <p className="text-center">⏳ Завантаження...</p>}
            {draftLoadNotice && (
                <Alert variant="info" className="mt-3" dismissible onClose={() => setDraftLoadNotice(null)}>
                    {draftLoadNotice}
                </Alert>
            )}
            {reportData && hasExtraFunds !== null && (
                <div className="text-center mt-3">
                    {hasExtraFunds ? (
                        <div className="alert alert-success" role="alert">
                            ✅ {t("extraCosts")} {t("loaded").toLocaleLowerCase()}
                        </div>
                    ) : (
                        <div className="alert alert-danger" role="alert">
                            ❌ {t("noRecords")} {t("extraCosts").toLocaleLowerCase()}{" "}
                            <a
                                href={`/admin/edit-extra-funds?driver_id=${selectedUser?.value}&week_number=${weekNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t("create")}
                            </a>
                        </div>
                    )}
                </div>
            )}

            {reportData && (
                <div id="report-content">
                    {selectedUser && weekNumber && (
                        <div className="text-center mt-3">
                            <h4 className="text-primary">
                                {t("raport")} {t("for").toLocaleLowerCase()} <strong>{selectedUser.label}</strong> {t("by").toLocaleLowerCase()} <strong>{reportDates}</strong>
                            </h4>
                        </div>
                    )}
                    {(() => {
                        const accounted = getDebtIdsAccountedInThisReport();
                        const openList = debtsEligibleForReportWeek.filter((d) => !accounted.has(String(d.id)));
                        if (openList.length === 0) return null;
                        return (
                            <div
                                className="alert alert-danger border-danger mt-3 text-start"
                                role="region"
                                aria-label={t("activeUnclosedDebtsTitle")}
                            >
                                <h5 className="alert-heading text-danger mb-3">{t("activeUnclosedDebtsTitle")}</h5>
                                <ul className="list-unstyled mb-0">
                                    {openList.map((debt) => (
                                        <li key={debt.id} className="mb-3 pb-3 border-bottom border-danger">
                                            <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                                                <div className="flex-grow-1 min-w-0">
                                                    <strong className="text-danger">
                                                        {debt.debt_name || `${t("debt")} #${debt.id}`}
                                                    </strong>
                                                    {debt.week_number != null && debt.week_number !== "" && (
                                                        <div className="small text-secondary mt-1">
                                                            {t("week")} {debt.week_number}
                                                        </div>
                                                    )}
                                                    <div className="small mt-1 text-danger" style={{ whiteSpace: "pre-wrap" }}>
                                                        {(debt.debt_create_desc && String(debt.debt_create_desc).trim()) || "—"}
                                                    </div>
                                                </div>
                                                <span className="badge bg-danger fs-6 align-self-start">
                                                    {Number(debt.debt_total || 0).toFixed(2)} PLN
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })()}
                    {renderTable("Uber", reportData.uber, "uber")}
                    {renderTable("Bolt", reportData.bolt, "bolt")}
                    {renderTable("FreeNow", reportData.freenow, "freenow")}
                    {renderFinalTable()}
                </div>
            )}

            {/* 💰 Блок розподілу виплати на зарплату */}
            {reportData && showSalaryDistribution && (
                <div className="card border-success mt-3 mb-3">
                    <div className="card-body">
                        <div className="row align-items-center">
                            {/* Ліва частина: сума до розподілу (може бути від'ємною) */}
                            <div className="col-md-4 mb-3 mb-md-0">
                                <p className="mb-1 text-muted small">{t("available")}:</p>
                                <h4 className="text-primary mb-0">{getTotalBeforeSalary().toFixed(2)} PLN</h4>
                            </div>
                            
                            {/* Центральна частина: інпут зарплати — можна ввести будь-яку суму >= 0 */}
                            <div className="col-md-4 mb-3 mb-md-0">
                                <label className="form-label small mb-1">
                                    {t("sallary")}:
                                </label>
                                <div className="input-group">
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        value={salaryInput}
                                        onChange={(e) => {
                                            const value = parseFloat(e.target.value) || 0;
                                            setSalaryInput(value >= 0 ? value : 0);
                                        }}
                                        min="0"
                                        step="10"
                                    />
                                    <span className="input-group-text">PLN</span>
                                </div>
                                <small className="text-muted">
                                    {getTotalBeforeSalary() >= 0
                                        ? `${t("max")}: ${getTotalBeforeSalary().toFixed(2)} PLN`
                                        : t("salaryIncreasesDebt")}
                                </small>
                            </div>
                            
                            {/* Права частина: фінальна виплата (сума до ЗП − ЗП, може бути від'ємною) */}
                            <div className="col-md-4">
                                <p className="mb-1 text-muted small">{t("payment")}:</p>
                                <h4 className="text-success mb-0">
                                    {(getTotalBeforeSalary() - parseFloat(salaryInput)).toFixed(2)} PLN
                                </h4>
                            </div>
                        </div>
                        
                        {/* Кнопка застосування */}
                        <div className="mt-3">
                            <button 
                                className="btn btn-success w-100" 
                                onClick={handleSaveSalary}
                                disabled={
                                    isSalaryUpdating || 
                                    parseFloat(salaryInput) === (parseFloat(extraFunds.sallary) || 0)
                                }
                            >
                                {isSalaryUpdating ? (
                                    <>⏳ {t("saving")}...</>
                                ) : (
                                    <>✅ {t("save")}</>
                                )}
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {reportData && (
                <div className="text-center mt-3 d-flex flex-wrap justify-content-center align-items-center gap-2">
                    <button type="button" className="btn btn-success" onClick={downloadPDF}>
                        {t("download")} PDF
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={downloadPDFReportV2}
                        disabled={pdfReportV2Loading}
                    >
                        {pdfReportV2Loading ? (
                            <><Spinner animation="border" size="sm" className="me-2" />PDF 2.0…</>
                        ) : (
                            <>PDF 2.0 (HTML)</>
                        )}
                    </button>
                    <button type="button" className="btn btn-warning" onClick={handleSaveDraft}>
                        💾 {t("save")} {t("draft").toLocaleLowerCase()}
                    </button>
                </div>
            )}

            <Modal
                show={showExistingReportModal}
                onHide={() => {
                    setShowExistingReportModal(false);
                }}
                size="lg"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("driverReportExistingTitle") || "Вже є збережений рапорт"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {existingReportRow && (() => {
                        const rj = existingReportRow.report_json || {};
                        const td = rj.taxData || {};
                        const ef = rj.extraFunds || {};
                        const st = existingReportRow.status;
                        const isPublished = st === "published";
                        return (
                            <div className="small">
                                <p className="mb-2">
                                    <span className="fw-semibold">{t("driver")}:</span>{" "}
                                    {existingReportRow.firstName} {existingReportRow.lastName}
                                    <span className="text-muted ms-2">(id {existingReportRow.driver_id})</span>
                                </p>
                                <p className="mb-2">
                                    <span className="fw-semibold">{t("week")}:</span> {existingReportRow.week_number}
                                </p>
                                <p className="mb-2">
                                    <span className="fw-semibold">{t("driverReportExistingRecordId") || "ID запису"}:</span>{" "}
                                    {existingReportRow.id}
                                    {" · "}
                                    <Link to={`/admin/driver-report/${existingReportRow.id}`} target="_blank" rel="noopener noreferrer">
                                        {t("driverReportExistingOpenPage") || "Відкрити сторінку рапорту"}
                                    </Link>
                                </p>
                                <p className="mb-2">
                                    <span className="fw-semibold">{t("status")}:</span>{" "}
                                    <span className={`badge ${isPublished ? "bg-success" : "bg-warning text-dark"}`}>
                                        {isPublished
                                            ? (t("published") || "опубліковано")
                                            : (t("draft") || "чернетка")}
                                    </span>
                                </p>
                                <p className="mb-2">
                                    <span className="fw-semibold">{t("created")}:</span>{" "}
                                    {formatReportDateTime(existingReportRow.created_at)}
                                </p>
                                <p className="mb-3">
                                    <span className="fw-semibold">{t("driverReportExistingUpdated") || "Останнє оновлення"}:</span>{" "}
                                    {formatReportDateTime(existingReportRow.updated_at)}
                                </p>
                                <hr />
                                <h6 className="fw-bold">{t("driverReportExistingTotals") || "Збережені підсумки"}</h6>
                                <ul className="mb-3">
                                    <li>
                                        <span className="fw-semibold">{t("payment")} (payout):</span>{" "}
                                        {rj.payout != null ? `${parseFloat(rj.payout).toFixed(2)} PLN` : "—"}
                                    </li>
                                    <li>
                                        <span className="fw-semibold">{t("debt")} ({t("driverReportExistingInReport") || "у рапорті"}):</span>{" "}
                                        {rj.totalDebt != null ? `${parseFloat(rj.totalDebt).toFixed(2)} PLN` : "—"}
                                    </li>
                                </ul>
                                <h6 className="fw-bold">{t("driverReportExistingTaxBlock") || "Податки та доходи по сервісах (збережено)"}</h6>
                                <Table bordered size="sm" className="mb-3">
                                    <thead>
                                        <tr>
                                            <th>{t("service")}</th>
                                            <th className="text-end">{t("taxes")} VAT 4%</th>
                                            <th className="text-end">{t("total")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {["uber", "bolt", "freenow"].map((svc) => (
                                            <tr key={svc}>
                                                <td className="text-capitalize">{svc}</td>
                                                <td className="text-end">{td[svc]?.tax != null ? Number(td[svc].tax).toFixed(2) : "—"}</td>
                                                <td className="text-end">{td[svc]?.total != null ? Number(td[svc].total).toFixed(2) : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <h6 className="fw-bold">{t("extraCosts")} ({t("driverReportExistingSaved") || "збережено"})</h6>
                                <Table bordered size="sm" className="mb-3">
                                    <tbody>
                                        {[
                                            ["returnVAT", `${t("return")} ${t("vat")}`],
                                            ["taxesZUS", `${t("taxes")} ${t("zus")}`],
                                            ["partner", t("partner")],
                                            ["accountant", t("accountant")],
                                            ["carRent", t("carRent")],
                                            ["other", t("other")],
                                            ["sallary", t("sallary")],
                                            ["refunds", t("refunds")],
                                        ].map(([key, label]) => (
                                            <tr key={key}>
                                                <td>{label}</td>
                                                <td className="text-end">{ef[key] != null && ef[key] !== "" ? String(ef[key]) : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <h6 className="fw-bold">{t("driverReportExistingDebtsTitle") || "Борги, враховані при збереженні (selectedDebts)"}</h6>
                                {Array.isArray(rj.selectedDebts) && rj.selectedDebts.length > 0 ? (
                                    <Table bordered size="sm" responsive>
                                        <thead>
                                            <tr>
                                                <th>id</th>
                                                <th>{t("debtTitle") || "Назва"}</th>
                                                <th className="text-end">{t("amount")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rj.selectedDebts.map((idRaw) => {
                                                const id = Number(idRaw);
                                                const d = existingReportDebts.find((x) => Number(x.id) === id);
                                                return (
                                                    <tr key={String(idRaw)}>
                                                        <td>{idRaw}</td>
                                                        <td>{d?.description || "—"}</td>
                                                        <td className="text-end">
                                                            {d?.amount != null ? `${Number(d.amount).toFixed(2)} PLN` : "—"}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <p className="text-muted mb-0">{t("driverReportExistingNoDebtsInJson") || "У збереженому JSON немає списку selectedDebts або він порожній."}</p>
                                )}
                                <p className="text-muted mt-3 mb-0">
                                    {t("driverReportExistingHint") ||
                                        "Нова генерація з сервісів не підтягує ці значення автоматично. Завантажте чернетку або продовжте генерацію свідомо."}
                                </p>
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer className="flex-wrap gap-2 justify-content-between">
                    <Button
                        variant="danger"
                        onClick={async () => {
                            setShowExistingReportModal(false);
                            setExistingReportRow(null);
                            setExistingReportDebts([]);
                            await runFetchReport();
                        }}
                    >
                        {t("driverReportExistingGenerateAnyway") || "Згенерувати заново (нові дані з сервісів)"}
                    </Button>
                    <div className="d-flex flex-wrap gap-2">
                        <Button
                            variant="primary"
                            onClick={() => existingReportRow && loadSavedDraftFromRow(existingReportRow)}
                        >
                            {t("driverReportExistingLoadDraft") || "Завантажити збережений рапорт"}
                        </Button>
                        <Button variant="secondary" onClick={() => setShowExistingReportModal(false)}>
                            {t("close")}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* 🔹 МОДАЛЬНЕ ВІКНО ДЛЯ БОРГІВ */}
            {showDebtModal && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content bg-danger text-white">
                            <div className="modal-header">
                                <h5 className="modal-title">{t("debtsMulti")}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowDebtModal(false)}></button>
                            </div>
                            <div className="modal-body bg-white text-dark">
                                {debtsEligibleForReportWeek.length > 0 ? (() => {
                                    const availableForPlan = round2(Math.max(0, calculatePreDebtPayout()));
                                    const plan = getDebtAllocationPlan(selectedDebts, debtsEligibleForReportWeek, availableForPlan);
                                    return (
                                    <>
                                        <div className="small text-muted mb-2 d-flex flex-wrap align-items-center gap-2">
                                            <span className="fw-semibold text-dark">{t("debtPlanAvailable", { amount: availableForPlan.toFixed(2), defaultValue: "Available: {{amount}} PLN" })}</span>
                                        </div>
                                        {selectedDebts.length > 0 && (
                                            <div className="small text-muted mb-3 d-flex flex-wrap align-items-center gap-x-3 gap-y-1">
                                                <span className="fw-semibold me-1">{t("debtPlanLegendTitle", { defaultValue: "План списання:" })}</span>
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    <span className="rounded border border-success" style={{ width: "0.75rem", height: "0.75rem", background: "#d1e7dd" }} aria-hidden />
                                                    {t("debtPlanFull", { defaultValue: "повне закриття" })}
                                                </span>
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    <span className="rounded border border-warning" style={{ width: "0.75rem", height: "0.75rem", background: "#fff3cd" }} aria-hidden />
                                                    {t("debtPlanPartial", { defaultValue: "часткове списання" })}
                                                </span>
                                                <span className="d-inline-flex align-items-center gap-1">
                                                    <span className="rounded border border-secondary" style={{ width: "0.75rem", height: "0.75rem", background: "#e9ecef" }} aria-hidden />
                                                    {t("debtPlanUnfunded", { defaultValue: "обрано, без коштів у цій виплаті" })}
                                                </span>
                                            </div>
                                        )}
                                    <ul className="list-group">
                                        {debtsEligibleForReportWeek.map((debt) => {
                                            const selected = isDebtSelected(debt.id);
                                            const role = selected ? plan.roleById[String(debt.id)] : undefined;
                                            let backgroundColor = "#f8f9fa";
                                            let borderColor = "#dee2e6";
                                            if (selected && role === "full") {
                                                backgroundColor = "#d1e7dd";
                                                borderColor = "#198754";
                                            } else if (selected && role === "partial") {
                                                backgroundColor = "#fff3cd";
                                                borderColor = "#ffc107";
                                            } else if (selected && role === "unfunded") {
                                                backgroundColor = "#e9ecef";
                                                borderColor = "#6c757d";
                                            } else if (selected) {
                                                backgroundColor = "#e9f5ff";
                                                borderColor = "#0d6efd";
                                            }
                                            const planLabel =
                                                role === "full"
                                                    ? t("debtPlanFull", { defaultValue: "повне закриття" })
                                                    : role === "partial"
                                                        ? t("debtPlanPartial", { defaultValue: "часткове списання" })
                                                        : role === "unfunded"
                                                            ? t("debtPlanUnfunded", { defaultValue: "обрано, без коштів у цій виплаті" })
                                                            : null;
                                            return (
                                            <li
                                                key={debt.id}
                                                className="list-group-item"
                                                style={{
                                                    borderWidth: "2px",
                                                    borderStyle: "solid",
                                                    borderColor,
                                                    borderRadius: "0.5rem",
                                                    backgroundColor,
                                                    marginBottom: "10px",
                                                    padding: "15px"
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="d-flex flex-column align-items-start gap-1 min-w-0 me-2">
                                                        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-1 gap-md-2 flex-wrap">
                                                            <strong className="me-2">{debt.debt_name}</strong>
                                                            <span className="badge bg-danger">{Number(debt.debt_total).toFixed(2)} PLN</span>
                                                            {planLabel && (
                                                                <span className="badge bg-dark bg-opacity-75 text-white fw-normal">{planLabel}</span>
                                                            )}
                                                        </div>
                                                        {(debt.debt_create_desc || debt.week_number != null) && (
                                                            <div className="small text-muted text-start w-100" style={{ whiteSpace: "pre-wrap" }}>
                                                                {debt.week_number != null && (
                                                                    <span className="text-secondary">{`${t("week")} ${debt.week_number}`}</span>
                                                                )}
                                                                {debt.week_number != null && debt.debt_create_desc && String(debt.debt_create_desc).trim() ? " · " : ""}
                                                                {(debt.debt_create_desc && String(debt.debt_create_desc).trim()) || (!debt.debt_create_desc && debt.week_number == null ? "—" : "")}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-flex align-items-center flex-shrink-0" style={{ gap: "0.5rem" }}>
                                                        <span
                                                            onClick={() => handleCloseDebt(debt.id)}
                                                            title={t("closeDebt")}
                                                            className="d-inline-flex align-items-center text-danger"
                                                            style={{
                                                                cursor: "pointer",
                                                                transition: "transform 0.2s",
                                                                userSelect: "none"
                                                            }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.15)"; }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                                                        >
                                                            <AiOutlineLock size={22} />
                                                        </span>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={selected}
                                                            onChange={() => toggleDebtSelection(debt)}
                                                            style={{ transform: "scale(1.4)", cursor: "pointer" }}
                                                        />
                                                    </div>
                                                </div>
                                            </li>
                                        );})}
                                    </ul>
                                    </>
                                    );
                                })() : (
                                    <p>{t("noRecords")}</p>
                                )}
                            </div>

                            <div className="modal-footer d-flex flex-wrap gap-2 align-items-stretch justify-content-end">
                                {(() => {
                                    const available = round2(Math.max(0, calculatePreDebtPayout()));
                                    const plan = getDebtAllocationPlan(selectedDebts, debtsEligibleForReportWeek, available);
                                    const hasUnfunded = selectedDebts.some((id) => plan.roleById[String(id)] === "unfunded");
                                    const canApply =
                                        selectedDebts.length > 0 &&
                                        !hasUnfunded &&
                                        plan.appliedTotal > EPSILON;
                                    const label = formatDebtApplyButtonLabel(plan, debtsEligibleForReportWeek);
                                    return (
                                        <button
                                            type="button"
                                            className="btn btn-primary me-auto text-wrap text-start"
                                            style={{ maxWidth: "100%", whiteSpace: "normal", lineHeight: 1.35 }}
                                            disabled={!canApply}
                                            title={
                                                hasUnfunded
                                                    ? t("debtApplyDisabledUnfunded", {
                                                          defaultValue:
                                                              "Є обрані борги без покриття цією виплатою — зніміть зайві галочки або зменшіть вибір.",
                                                      })
                                                    : selectedDebts.length === 0
                                                      ? t("debtApplyDisabledNone", { defaultValue: "Оберіть борг(и)" })
                                                      : plan.appliedTotal <= EPSILON
                                                        ? t("debtApplyDisabledNoFunds", { defaultValue: "Немає доступної суми для списання" })
                                                        : undefined
                                            }
                                            onClick={applySelectedDebts}
                                        >
                                            {label}
                                        </button>
                                    );
                                })()}
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDebtModal(false)}>
                                    {t("close")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Модальне вікно: завантаження попереднього перегляду — спочатку загальні кроки, потім по кожному водію */}
            {showBatchPreviewLoading && (() => {
                const isPerDriver = batchPreviewPhase === "per_driver";
                const drivers = (batchPreviewApiResult?.drivers || []);
                const currentDriver = drivers[batchPreviewDriverIndex] || null;
                const totalTicks = 2 + drivers.length * BATCH_PREVIEW_DRIVER_STEPS.length;
                const currentTick = isPerDriver
                    ? 2 + batchPreviewDriverIndex * BATCH_PREVIEW_DRIVER_STEPS.length + batchPreviewDriverStepIndex + 1
                    : batchPreviewStepIndex + 1;
                const progressPercent = totalTicks > 0 ? (currentTick / totalTicks) * 100 : 0;

                return (
                    <Modal show centered backdrop="static" className="batch-preview-loading-modal">
                        <Modal.Body className="p-4 text-center">
                            <h5 className="mb-4 fw-bold text-primary">
                                {t("batchPreviewLoadingTitle") || "Підготовка попереднього перегляду"}
                            </h5>
                            <p className="text-muted small mb-3 d-flex align-items-center justify-content-center gap-2 flex-wrap">
                                {isPerDriver && currentDriver ? (
                                    <>
                                        <span>{(t("batchPreviewCheckingDriver") || "Перевіряємо водія:") + " " + `${currentDriver.firstName} ${currentDriver.lastName}`}</span>
                                        {batchPreviewDriverStepIndex >= 4 && (() => {
                                            const hasUber = !!currentDriver.has_uber;
                                            const hasBolt = !!currentDriver.has_bolt;
                                            const hasFreenow = !!currentDriver.has_freenow;
                                            const hasDebts = (currentDriver.open_debts?.length || 0) > 0;
                                            const allOk = hasUber && hasBolt && hasFreenow && !hasDebts;
                                            return allOk ? (
                                                <span className="text-success" title={t("batchDriverResultOk") || "Усе добре"}>✓</span>
                                            ) : (
                                                <span className="text-warning" title={t("batchDriverResultIssues") || "Є зауваження (немає даних сервісу або є борги)"}>⚠</span>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    (t("batchPreviewLoadingSubtitle") || "Перевіряємо водіїв та дані за тиждень…")
                                )}
                            </p>
                            <div className="progress mb-4" style={{ height: "8px" }}>
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                    role="progressbar"
                                    style={{ width: `${progressPercent}%`, transition: "width 0.35s ease-out" }}
                                    aria-valuenow={currentTick}
                                    aria-valuemin={0}
                                    aria-valuemax={totalTicks}
                                />
                            </div>
                            {!isPerDriver ? (
                                <ul className="list-unstyled text-start mx-auto" style={{ maxWidth: "320px" }}>
                                    {BATCH_PREVIEW_INITIAL_STEPS.map((step, index) => {
                                        const isActive = index === batchPreviewStepIndex;
                                        const isDone = index < batchPreviewStepIndex;
                                        return (
                                            <li key={step.key} className={`d-flex align-items-center gap-2 py-2 px-2 rounded ${isActive ? "bg-light" : ""}`}>
                                                {isDone ? (
                                                    <span className="text-success flex-shrink-0" style={{ width: "24px" }}>✓</span>
                                                ) : isActive ? (
                                                    <Spinner animation="border" size="sm" className="flex-shrink-0 text-primary" style={{ width: "24px", height: "24px" }} />
                                                ) : (
                                                    <span className="text-muted flex-shrink-0" style={{ width: "24px", fontSize: "0.7rem" }}>○</span>
                                                )}
                                                <span className={isDone ? "text-muted" : ""}>{t(step.labelKey) || step.labelKey}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <ul className="list-unstyled text-start mx-auto" style={{ maxWidth: "320px" }}>
                                    {BATCH_PREVIEW_DRIVER_STEPS.map((step, index) => {
                                        const isActive = index === batchPreviewDriverStepIndex;
                                        const isDone = index < batchPreviewDriverStepIndex;
                                        const stepIsGood = !currentDriver ? true : (
                                            step.dataKey === null ? true :
                                            step.invert
                                                ? !((currentDriver[step.dataKey]?.length ?? 0) > 0)
                                                : !!currentDriver[step.dataKey]
                                        );
                                        const statusClass = (isDone || isActive) ? (stepIsGood ? "text-success" : "text-danger fw-semibold") : "";
                                        return (
                                            <li
                                                key={step.key}
                                                className={`d-flex align-items-center gap-2 py-2 px-2 rounded ${isActive ? "bg-light" : ""} ${statusClass}`}
                                                style={{ borderLeft: `3px solid ${(isDone || isActive) ? (stepIsGood ? "var(--bs-success)" : "var(--bs-danger)") : "transparent"}` }}
                                            >
                                                {isDone ? (
                                                    <span className={`flex-shrink-0 ${stepIsGood ? "text-success" : "text-danger"}`} style={{ width: "24px" }}>
                                                        {stepIsGood ? "✓" : "✗"}
                                                    </span>
                                                ) : isActive ? (
                                                    <Spinner animation="border" size="sm" className={`flex-shrink-0 ${stepIsGood ? "text-success" : "text-danger"}`} style={{ width: "24px", height: "24px" }} />
                                                ) : (
                                                    <span className="text-muted flex-shrink-0" style={{ width: "24px", fontSize: "0.7rem" }}>○</span>
                                                )}
                                                <span>{t(step.labelKey) || step.labelKey}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </Modal.Body>
                    </Modal>
                );
            })()}

            {/* Модальне вікно: batch створення чернеток */}
            {showBatchModal && batchPreview && (() => {
                const drivers = batchPreview.drivers || [];
                const negativePayout = (d) => {
                    const after = (d.payout_no_debt || 0) - (d.total_debt_sum || 0);
                    return (d.open_debts && d.open_debts.length > 0) ? after < 0 : (d.payout_no_debt || 0) < 0;
                };
                const getSelectedDebtSum = (driver) => {
                    const ids = batchSelectedDebtIds[driver.driver_id] || [];
                    return (driver.open_debts || []).filter((x) => ids.includes(x.id)).reduce((s, x) => s + (x.debt_total || 0), 0);
                };
                const sortedDrivers = [...drivers].sort((a, b) => {
                    const aNeg = negativePayout(a) ? 0 : (a.open_debts && a.open_debts.length > 0) ? 1 : 2;
                    const bNeg = negativePayout(b) ? 0 : (b.open_debts && b.open_debts.length > 0) ? 1 : 2;
                    if (aNeg !== bNeg) return aNeg - bNeg;
                    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
                });
                const toggleDebtInBatch = (driverId, debtId) => {
                    setBatchSelectedDebtIds((prev) => {
                        const current = prev[driverId] || [];
                        const has = current.includes(debtId);
                        return { ...prev, [driverId]: has ? current.filter((id) => id !== debtId) : [...current, debtId] };
                    });
                };

                const renderDriverRow = (d) => {
                    const selectedSum = getSelectedDebtSum(d);
                    const payoutAfterDebt = (d.payout_no_debt || 0) - selectedSum;
                    const hasDebts = d.open_debts && d.open_debts.length > 0;
                    const isNegative = hasDebts ? payoutAfterDebt < 0 : (d.payout_no_debt || 0) < 0;
                    return (
                        <li
                            key={d.driver_id}
                            className="list-group-item d-flex align-items-start justify-content-between gap-3 py-3"
                            style={{ borderLeft: `4px solid ${isNegative ? "var(--bs-warning)" : hasDebts ? "var(--bs-danger)" : "var(--bs-secondary)"}` }}
                        >
                            <div className="d-flex flex-column min-w-0 flex-grow-1">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {hasDebts ? (
                                        <AiOutlineFolderOpen size={20} className="text-danger flex-shrink-0" />
                                    ) : (
                                        <AiOutlineCheckCircle size={20} className="text-success flex-shrink-0" />
                                    )}
                                    <span className={hasDebts ? "fw-bold text-dark" : "fw-semibold"}>
                                        {d.firstName} {d.lastName}
                                    </span>
                                    {hasDebts && (
                                        <span className="badge bg-danger rounded-pill">
                                            {Number(d.total_debt_sum || 0).toFixed(2)} PLN
                                        </span>
                                    )}
                                    {isNegative && (
                                        <span className="badge bg-warning text-dark rounded-pill">
                                            {t("batchNegativePayout") || "негативна виплата"}
                                        </span>
                                    )}
                                </div>
                                <div className="small text-muted mt-2">
                                    <span>{t("batchPayoutNoDebt") || "Виплата без боргу"}: <strong className="text-dark">{Number(d.payout_no_debt || 0).toFixed(2)} PLN</strong></span>
                                    {hasDebts && (
                                        <>
                                            <span className="mx-1">·</span>
                                            <span className={payoutAfterDebt < 0 ? "text-danger fw-bold" : ""}>
                                                {t("batchPayoutAfterDebt") || "Після списання"}: {payoutAfterDebt.toFixed(2)} PLN
                                            </span>
                                        </>
                                    )}
                                </div>
                                {(() => {
                                    const selectedCount = (batchSelectedDebtIds[d.driver_id] || []).length;
                                    if (payoutAfterDebt < 0) {
                                        const debtAmount = Math.abs(payoutAfterDebt).toFixed(2);
                                        return (
                                            <div className="small mt-1 text-warning-emphasis">
                                                {t("batchOutcomeDebtCreated", { amount: debtAmount }) || `При збереженні буде створено новий борг на суму ${debtAmount} PLN (як при ручному створенні).`}
                                            </div>
                                        );
                                    }
                                    if (hasDebts && selectedCount > 0) {
                                        return (
                                            <div className="small mt-1 text-success-emphasis">
                                                {t("batchOutcomeDebtsClosed") || "Обрані борги будуть закриті в рапорті."}
                                                {selectedCount < (d.open_debts?.length || 0) && (
                                                    <span className="text-muted"> ({t("batchOutcomePartClosed") || "частина залишиться відкритою"})</span>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (hasDebts && selectedCount === 0) {
                                        return (
                                            <div className="small mt-1 text-muted">
                                                {t("batchOutcomeDebtsUnchanged") || "Борги не списуються — залишаться відкритими."}
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="small mt-1 text-muted">
                                            {t("batchOutcomeNoDebt") || "Виплата без змін, боргів немає."}
                                        </div>
                                    );
                                })()}
                                {hasDebts && d.open_debts.length > 0 && (
                                    <div className="mt-2 ps-2 border-start border-2 border-light">
                                        <span className="small text-secondary">{t("batchApplyDebtInReport") || "Списати в рапорті"}:</span>
                                        {d.open_debts.map((debt) => (
                                            <div key={debt.id} className="form-check form-check-inline mt-1 me-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id={`batch-debt-${d.driver_id}-${debt.id}`}
                                                    checked={(batchSelectedDebtIds[d.driver_id] || []).includes(debt.id)}
                                                    onChange={() => toggleDebtInBatch(d.driver_id, debt.id)}
                                                />
                                                <label className="form-check-label small" htmlFor={`batch-debt-${d.driver_id}-${debt.id}`}>
                                                    {debt.debt_name || t("debt")} — {Number(debt.debt_total || 0).toFixed(2)} PLN
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="d-flex align-items-center flex-shrink-0">
                                <div className="form-check mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`batch-include-${d.driver_id}`}
                                        checked={batchDriverInclude[d.driver_id] !== false}
                                        onChange={() => setBatchDriverInclude((prev) => ({ ...prev, [d.driver_id]: !prev[d.driver_id] }))}
                                    />
                                    <label className="form-check-label small" htmlFor={`batch-include-${d.driver_id}`}>
                                        {t("batchCreateReport") || "Створювати рапорт"}
                                    </label>
                                </div>
                            </div>
                        </li>
                    );
                };

                return (
                    <Modal show onHide={() => { setShowBatchModal(false); setBatchPreview(null); setBatchResult(null); }} size="lg" centered scrollable>
                        <Modal.Header closeButton className="border-bottom-2">
                            <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
                                {t("batchCreateDrafts") || "Створити чернетки для всіх"}
                                <span className="badge bg-primary rounded-pill fs-6">
                                    {batchPreview.summary?.total_drivers ?? 0} {t("driver")}
                                </span>
                                {batchPreview.summary?.drivers_with_debts > 0 && (
                                    <span className="badge bg-danger rounded-pill fs-6">
                                        {batchPreview.summary.drivers_with_debts} {t("batchWithDebts") || "з боргами"}
                                    </span>
                                )}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="p-0">
                            <div className="p-3 pb-0">
                                <p className="text-muted small mb-2">
                                    {t("batchPreviewSummary") || "Чернетки рапортів для водіїв (роль user) з додатковими коштами та даними з сервісів."} {(t("week") || "").toLowerCase()}: <strong>{weekNumber}</strong>
                                </p>
                            </div>
                            {sortedDrivers.length > 0 && (
                                <ul className="list-group list-group-flush">
                                    {sortedDrivers.map(renderDriverRow)}
                                </ul>
                            )}
                            {batchResult && (
                                <div className="p-3">
                                    <Alert variant="info" className="mb-0 small">
                                        {t("batchResult") || "Результат"}: <strong>{batchResult.created ?? 0}</strong> {t("batchCreated") || "створено"}, <strong>{batchResult.updated ?? 0}</strong> {t("batchUpdated") || "оновлено"}, <strong>{batchResult.debt_closed_count ?? 0}</strong> {t("closed")} {t("debt").toLowerCase()}
                                        {(batchResult.debts_created ?? 0) > 0 && <>, <strong>{batchResult.debts_created}</strong> {t("batchDebtsCreated") || "боргів створено"}</>}.
                                        {batchResult.errors && batchResult.errors.length > 0 && (
                                            <div className="text-danger mt-1">{batchResult.errors.length} {t("batchErrors") || "помилок"}</div>
                                        )}
                                    </Alert>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer className="border-top">
                            <Button variant="secondary" onClick={() => { setShowBatchModal(false); setBatchPreview(null); setBatchResult(null); }}>
                                {t("close")}
                            </Button>
                            <Button variant="primary" onClick={handleBatchCreate} disabled={batchSaving}>
                                {batchSaving ? (t("saving") || "Збереження...") : (t("batchConfirmCreate") || "Створити чернетки")}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                );
            })()}

        </div>
    );
};

export default DriverRaport;
